/**
 * Занятие · Ф2.3 — боевой цикл: `GET /session` → карточка → раскрытие → оценка 0/1/2 →
 * `POST /answer` → следующая → сводка подхода.
 *
 * ⛔ Здесь НЕТ ни SRS, ни очереди, ни лимитов: всё это считает ядро
 * (`runner.build_session` / `runner.answer` / `scheduler`), витрина только рисует и
 * передаёт оценку. Сторона вопроса (`direction`) тоже приходит от API — она выведена
 * из зрелости слова тем же порогом, что у консольного раннера (бокс 0–1 EN→RU,
 * 2+ RU→EN).
 *
 * ⭐⭐ ОКНО М1 (27.07): вид карточки БОЛЬШЕ НЕ НАСТРОЙКА, а ступень лестницы подачи —
 * её считает `src/ladder.ts` по зрелости слова (бокс Leitner). Раньше человек выбирал
 * вид сам, и «Собери фразу» доставалась новому слову — то есть продукция до узнавания.
 * Ручной выбор остался отладочным тумблером (`delivery=manual`), методика — дефолт.
 *
 * Пять готовых видов (все живы с окна 3) — это ТЕЛА ступеней:
 *   • «Тишина»  — только слово, ничего лишнего → раскрытие → самооценка;
 *   • «Досье»   — слово, транскрипция, часть речи → раскрытие → самооценка;
 *   • «Выбор»   — угадать перевод из четырёх (на готовых `lexicon.distractors`);
 *   • «Собери фразу» — выложить предложение кнопками-словами, среди них лишние
 *     (логика — `src/build.ts`): проверяются форма И порядок, клавиатура не нужна;
 *   • «Контекст» — впечатать пропущенное слово в предложение со сверкой ПО ФОРМЕ
 *     (`has _____` → `gone`, а не `go`; логика — `src/grammar.ts`);
 *   • ⭐ «На слух» (окно М2, ступень 1а) — звучит слово, ТЕКСТА НЕТ, выбираешь перевод
 *     из четырёх; написание появляется вместе с разбором, после ответа;
 *   • ⭐⭐ «Пропуск» (окно М3, ступень 3) — короткий пример с пропуском и его перевод,
 *     ответ выбирается кнопкой: нужная форма · лемма · другие формы (логика — `src/gap.ts`).
 *     Форма проверяется так же жёстко, как в «Контексте», но без клавиатуры. ⭐ У
 *     СЛУЖЕБНОГО слова кнопками идут другие служебные слова — «would» спрашивается
 *     конструкцией, а не переводом («бы» — обломок, а не перевод; решает ядро полем
 *     `function_word`). Он же показывает в разборе ДЛИННЫЙ пример вторым, с бокса 2;
 *   • ⭐⭐ «Фраза на слух» (окно М5, ступень 5) — то же тело, что у «Пропуска», но
 *     предложение ЗВУЧИТ целиком, материал длинный, а перевод придержан до разбора:
 *     проверяется аудирование ПЛЮС форма;
 *   • ⭐⭐ «Беглость» (окно М6, ступень 6) — слово, ВИДИМЫЙ таймер 5 секунд и два ответа
 *     «Помню / не помню». Оценку выводит ВРЕМЯ: быстрее 3 с → 2, 3–5 с → 1, не успел → 0.
 *     Разбор здесь урезан до трёх строк (слово · перевод · звук): ступень меряет темп.
 *     ⛔ «Тишина» при этом жива и не тронута — она осталась ручным отладочным видом.
 * У «Выбора», «Собери фразу» и «Контекста» оценка ВЫВОДИТСЯ из ответа: верно сразу → 2,
 * верно со второй попытки → 1, мимо → 0. У «Тишины» и «Досье» человек судит себя сам.
 *
 * Всё состояние подхода живёт в модуле, а не в DOM: экран можно перерисовать в любой
 * момент (смена темы, обновление цифр) и он соберётся тем же самым — включая то, что
 * уже напечатано в пропуске.
 */

import "../styles/session.css";

import * as api from "../api";
import type { AnswerOut, Card, DailyStats } from "../api";
import * as audio from "../audio";
import { type BuildSpec, buildSpec, checkBuild, gradeFor } from "../build";
import { type ChoiceSpec, choiceSpec } from "../choice";
import { burst, clearFx, praise, salute } from "../fx";
import { type GapSpecChoice, checkChoice, gapChoice, isFormMiss, opensRuAfterMiss,
         phraseGap, showsPhraseRu } from "../gap";
import { FORM_LABEL, type GapSpec, formList, gapSpec, hasIrregularForms,
         isBlankForm, mask, check } from "../grammar";
import * as extra from "../extra";
import { noteCard } from "../feedback";
import * as ladder from "../ladder";
import { drawMascot } from "../mascot";
import { render } from "../router";
import { state, xp as xpState } from "../state";
import { noteLine, registerLine, softerLine } from "../packs";
import { tap } from "../tg";
import { gainLabel, mascotLevel, toNextLabel } from "../xp";

/* ── что умеет показывать экран ───────────────────────────────────────────── */

type Phase = "idle" | "loading" | "card" | "done" | "empty" | "error";
type Mode = ladder.Mode;

const MODE_TITLE: Record<Mode, string> = {
  silence: "Тишина", dossier: "Досье", choice: "Выбор",
  build: "Собери фразу", context: "Контекст", listen: "На слух", gap: "Пропуск",
  hear: "Фраза на слух", fluency: "Беглость",
};

/** Части речи по-русски: то же соответствие, что печатает консольный раннер. */
const POS_RU: Record<string, string> = {
  verb: "глагол", noun: "существительное", adjective: "прилагательное",
  adverb: "наречие", preposition: "предлог", pronoun: "местоимение",
  conjunction: "союз", determiner: "определитель", numeral: "числительное",
  interjection: "междометие", phrase: "выражение",
};

/* ── состояние подхода ────────────────────────────────────────────────────── */

let phase: Phase = "idle";
let deck: Card[] = [];
let idx = 0;
let startStats: DailyStats | null = null;
let failure = "";

// состояние текущей карточки
let revealed = false;                 // «Тишина»/«Досье»: ответ раскрыт
let introDone = false;                // ⭐ М1: знакомство закрыто — та же карточка идёт на проверку
let wrongPicks: string[] = [];        // «Выбор»: промахи по этой карточке
let settled = false;                  // «Выбор»/«Контекст»: исход определён
let sentGrade: 0 | 1 | 2 | null = null;
let autoGrade: 0 | 1 | 2 | null = null;   // оценка, выведенная из ответа
let sending = false;
let sendError = "";
let lastAnswer: AnswerOut["state"] | null = null;

// «Собери фразу»: что выложено (номера кнопок в наборе), сколько было промахов, что сказать
let picked: number[] = [];
let bTries = 0;
let bHint = "";
let bOk: boolean | null = null;

// ⭐ М3 «Пропуск» (ступень 3): что уже нажали мимо и что сказать про промах
let gPicks: string[] = [];
let gHint = "";
// ⭐ М5.1 «Фраза на слух» (ступень 5): открыт ли перевод фразы — опора по требованию.
// Состояние живёт РОВНО на карточку (сбрасывается в `resetCard`), как `listenSeen` на 1а.
let hearRu = false;

// «Контекст»: что напечатано в пропусках, сколько было проверок, что сказать в ответ
let gapText: string[] = [];
let gapTries = 0;
let gapHint = "";                     // готовая разметка подсказки под пропуском
let gapOk: boolean | null = null;     // исход сверки (null — ещё не сверяли)
let gapMiss = -1;                     // какое место не сошлось на первой попытке

// звук (Ф-D): проговорили ли слово само на этой карточке и чью озвучку уже тянем заранее
let spoken = false;
let preloadedFor = -1;

// «На слух» (М2): показали ли написание до ответа и проверяли ли уже, что файл вообще есть
let listenSeen = false;
let listenProbed = false;
// ⭐ М5 «Фраза на слух» (ступень 5): проверяли ли уже, что файл ФРАЗЫ доехал
let hearProbed = false;
// ⭐ Обкатка 28.07: прозвучало ли слово на этой карточке хоть раз. От этого зависит ОДНА
// подпись на единственной кнопке звука: «Прослушать» → «Ещё раз».
let listenHeard = false;

/* ⭐⭐ М6 «Беглость» (ступень 6): секундомер карточки. Отсчёт живёт в модуле, а не в DOM,
   потому что экран перерисовывается часто, а мерить надо ОДИН заход. `flStart` = момент,
   когда карточка встала перед человеком; `flMs` = сколько он думал (null — не ответил
   вовсе); `flOut` = время вышло; `flTimer` — id тикающего интервала. */
let flStart = 0;
let flMs: number | null = null;
let flOut = false;
let flTimer = 0;

// итоги подхода
let tally = { total: 0, easy: 0, hard: 0, again: 0 };
/* ⭐ М6, пункт 2: результат СЕРИИ беглости. ⛔ В БД не уезжает и схему не трогает —
   скорость это свойство ЗАХОДА, а не слова, и хранить её негде (да и незачем: показать
   результат серии можно и так). Живёт ровно столько, сколько живёт подход. */
let fluency = { total: 0, fast: 0, timed: 0, sumMs: 0 };
let earnedAll: { code: string; title: string }[] = [];
/* ⭐ Ф2.5: опыт за подход. Копим ПРИБАВКИ, которые прислало ядро на каждый ответ
   (`/answer` → `xp.gained`), — витрина сама ничего не начисляет и не считает уровень. */
let xpRun = { gained: 0, levelUp: 0 };
let pending: Card[] = [];             // что ядро даст на следующий подход
let pendingStats: DailyStats | null = null;
// ⭐ «Ещё 5 новых»: что сказать про добавку. Пусто = сказать нечего.
let extraNote = "";
let extraBusy = false;

/* ── мелкие помощники ─────────────────────────────────────────────────────── */

/** Данные приходят из базы (их писал LLM) — в разметку только экранированными. */
function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (ch) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch] as string));
}

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/** Лестница включена? (⛔ выключается только отладочным тумблером `delivery=manual`) */
function ladderOn(): boolean {
  return ladder.isLadder(state.me?.settings.delivery);
}

/**
 * ⭐ М2: можно ли задать вопрос ЗВУКОМ — три условия, все проверяются до отрисовки:
 * звук не выключен настройкой · у слова есть `lexicon_id` (адрес файла) · про этот файл
 * ещё не известно, что его нет (404 плеер запоминает). Не выполнилось — ступень 1а
 * молча уступает ступени 1, человек ничего не замечает.
 *
 * ⛔ Отказ браузера в АВТОзапуске сюда не входит: он не значит «звука не будет» —
 * по нажатию «Прослушать» запись играет всегда (первое же касание снимает запрет).
 * Такую карточку мы не отбираем, а честно подписываем «звук — по нажатию».
 */
function canListen(c: Card): boolean {
  if (audio.prefs().mode === "off" || !c.lexicon_id) return false;
  return !audio.missing(audio.ref("word", c.lexicon_id));
}

/**
 * ⭐ М3: собирается ли из готовых данных ступень 3 «пропуск выбором». Кнопками идут формы
 * слова, а у служебного — другие служебные слова; чего не хватило, добираем словами
 * соседних карточек подхода (у 61% активной полосы своих форм нет вовсе).
 */
function gapFor(c: Card): GapSpecChoice | null {
  return gapChoice(c, neighbourWords(c));
}

/** Слова соседних карточек подхода (сперва той же части речи) — ими добираем кнопки. */
function neighbourWords(c: Card): string[] {
  const others = deck.filter((x) => x.word_id !== c.word_id);
  const samePos = others.filter((x) => x.part_of_speech === c.part_of_speech);
  return [...samePos, ...others].map((x) => x.word).filter(Boolean);
}

/**
 * ⭐⭐ М5: собирается ли ступень 5 «фраза на слух». Задание то же, что у ступени 3, но
 * материал ДЛИННЫЙ (его отдаёт ядро само с бокса 4) и есть потолок длины — предложение
 * держат в голове целиком, пока ищут пропуск.
 */
function hearFor(c: Card): GapSpecChoice | null {
  return phraseGap(c, neighbourWords(c));
}

/**
 * ⭐ М5: можно ли задать вопрос ФРАЗОЙ. Три условия, ровно как у ступени 1а, только про
 * пример: включён режим «Слово и фраза» (в режиме «Слово» фраз нет вовсе) · у карточки
 * есть `example_id` · про этот файл ещё не известно, что его нет. Не выполнилось —
 * ступень 5 молча уступает ступени 4.
 */
function canHear(c: Card): boolean {
  if (audio.prefs().mode !== "word_phrase" || !c.example_id) return false;
  return !audio.missing(audio.ref("phrase", c.example_id));
}

/** Ступень слова по его зрелости — для подписи подхода и связки «знакомство → проверка». */
function step(c: Card): ladder.Step {
  return ladder.stepFor(c, canListen(c), !!gapFor(c), canHear(c) && !!hearFor(c));
}

/**
 * ⭐ М1: карточка на ЗНАКОМСТВЕ — слово ни разу не отвечено, показываем его целиком,
 * а спрашиваем следом в этой же карточке. Условие «есть куда вести» обязательно: если
 * проверка из данных не собирается, знакомство остаётся самооценкой, как было (пустых
 * экранов человек видеть не должен).
 *
 * ⭐ М3: куда именно ведёт знакомство, зависит от слова — обычное идёт на выбор перевода,
 * служебное сразу на пропуск («would → бы» не спрашиваем никогда).
 */
function afterIntroMode(c: Card): Mode {
  return ladder.afterIntro(c, !!gapFor(c));
}

function inIntro(c: Card): boolean {
  if (!ladderOn() || !step(c).intro) return false;
  const next = afterIntroMode(c);
  return next === "gap" ? !!gapFor(c) : !!choiceOptions(c);
}

/**
 * Вид, которым карточка ПОКАЖЕТСЯ. Два слоя:
 *   1) ступень лестницы по зрелости слова (`ladder.modeFor`) — или ручной выбор в отладке;
 *   2) спуск НИЖЕ, если вид не собирается из готовых данных (нет четырёх вариантов /
 *      нет заготовки пропуска): пустых полей человек видеть не должен.
 *
 * ⭐⭐ ЗАКОН ОКНА 2.1 (26.07, возражение владельца принято): «Контекст» с вводом даётся
 * ТОЛЬКО продукционной стороне — слову бокса 2+ (`direction === "ru_en"`). Спрашивать
 * форму у слова, которое человек видит ВПЕРВЫЕ, — это продукция до узнавания: формы
 * неправильного глагола ниоткуда не выводятся, их надо сперва увидеть. Правило совпадает
 * с `direction` от ядра — отдельного закона витрина не заводит. Лестница его не отменяет,
 * а усиливает: «Контекст» теперь начинается с бокса 3 (`ladder.WRITE_BOX`).
 */
function shownMode(c: Card): Mode {
  const want = ladder.modeFor(c, state.me?.settings.delivery, state.me?.settings.card_mode,
                              canListen(c), !!gapFor(c), canHear(c) && !!hearFor(c));
  // Знакомство закрыто нажатием «Дальше» → та же карточка идёт следующей ступенью.
  const m: Mode = introDone && inIntro(c) ? afterIntroMode(c) : want;
  // ⭐ М2: «На слух» — тот же выбор из четырёх, что и «Выбор», только вопрос звучит.
  // Не собрались варианты — падаем туда же, куда падает «Выбор».
  if (m === "listen") return choiceOptions(c) ? "listen" : "dossier";
  // ⭐ М5: «Фраза на слух» — страховка на случай, когда файл фразы отвалился уже ПОСЛЕ
  // того, как ступень выбрали (плеер узнаёт о 404 не сразу). Ступень ниже — это «Контекст»,
  // а если и он не собирается, дальше по той же цепочке, что у него.
  if (m === "hear" && !(canHear(c) && hearFor(c))) {
    return c.direction === "ru_en" && gapSpec(c) ? "context"
         : choiceOptions(c) ? "choice" : "dossier";
  }
  // ⭐ М3: пропуск не собрался — ступень ниже. Но служебному слову «ниже» это НЕ выбор
  // перевода: у `would` перевода нет, поэтому карточка честно остаётся знакомством.
  if (m === "gap") {
    if (gapFor(c)) return "gap";
    return ladder.byConstruction(c) ? "dossier" : choiceOptions(c) ? "choice" : "dossier";
  }
  if (m === "choice") return choiceOptions(c) ? "choice" : "dossier";
  if (m === "build") return buildFor(c) ? "build" : choiceOptions(c) ? "choice" : "dossier";
  if (m === "context") {
    if (c.direction !== "ru_en" || !gapSpec(c)) return choiceOptions(c) ? "choice" : "dossier";
    return "context";
  }
  return m;
}

/**
 * ⭐ КОМУ ДАЁТСЯ «Собери фразу» — ОБЕИМ сторонам, и новому слову тоже.
 *
 * Закон окна 2.1 запретил «Контекст» новому слову не из-за предложения с пропуском, а
 * из-за ПРОДУКЦИИ: достать слово из головы и написать его в нужной форме нельзя, пока
 * слово не узнаёшь. Здесь этого спроса нет — все слова лежат на экране кнопками, работа
 * человека = узнать нужное и выложить порядок, то есть УЗНАВАНИЕ, ровно как в «Выборе».
 * Плюс новому слову мы честно даём дозу ДО спроса (столп №2 базы знаний): лемма,
 * транскрипция и перевод стоят над заданием, а вывести из леммы нужную форму — это уже
 * работа, а не угадывание. У окрепшего слова дозы нет: там оно и должно вспоминаться само.
 *
 * Набор кнопок собирается из ГОТОВЫХ данных: заготовка пропуска + формы слова + слова
 * соседних карточек подхода. Не собралось — карточка честно уходит «Выбором».
 */
function buildFor(c: Card): BuildSpec | null {
  const others = deck.filter((x) => x.word_id !== c.word_id);
  const samePos = others.filter((x) => x.part_of_speech === c.part_of_speech);
  const pool = [...samePos, ...others].map((x) => x.word).filter(Boolean);
  return buildSpec(c, pool);
}

/** Ответ уже на экране? У «Тишины»/«Досье» это раскрытие, у «Выбора»/«Контекста» — исход. */
function opened(): boolean {
  return revealed || settled;
}

/* ── звук (Ф-D) ───────────────────────────────────────────────────────────── */

/**
 * Кнопка звука. Вид 1:1 из макета `design/audio-button-variants.html`:
 * `round` — волна под словом (Т2) · `plain` — иконка в строке с транскрипцией (Д1) ·
 * `tiny` — у правильного варианта (В2) · `pill` — «Фраза» после сверки (К1).
 * ⭐ М2: `big` — крупная кнопка «Прослушать» на карточке «На слух»: там звук не добавка
 * к слову, а САМ ВОПРОС, поэтому кнопка главная на экране и промахнуться по ней нельзя.
 * Пока играет, иконку подменяет волна — это делает CSS по классу `on`.
 */
function snd(ref: string, look: "round" | "plain" | "tiny" | "pill" | "big", label: string,
             text = "", extra = ""): string {
  const speaker = '<path d="M4 9v6h3l5 4V5L7 9H4Z"/>';
  const arcs = ref.startsWith("phrase")
    ? '<path d="M15 8a6 6 0 0 1 0 8"/><path d="M18.5 5.5a10 10 0 0 1 0 13"/>'
    : '<path d="M16 8.5a5 5 0 0 1 0 7"/>';
  // ⭐⭐ Окно 1 запуска: ВТОРАЯ иконка — «следующее нажатие медленное». Обе лежат в разметке
  // всегда, показывает нужную CSS по классу `slow` (его ставит `audio.sync`): подменять
  // разметку на лету нельзя — кнопка живёт сквозь перерисовки и посреди проигрывания.
  // Рисунок отличается ровно настолько, чтобы разница читалась: волна короче и с точкой —
  // «то же самое, но растянуто».
  const slowArcs = '<path d="M15.5 9.8a3 3 0 0 1 0 4.4"/><circle cx="19.2" cy="12" r="1.1"/>';
  return `<button class="snd ${look}${extra ? " " + extra : ""}" type="button" data-snd="${esc(ref)}" ` +
    `aria-label="${esc(label)}" aria-pressed="false">` +
    `<svg viewBox="0 0 24 24">${speaker}` +
    `<g class="fastarc">${arcs}</g><g class="slowarc">${slowArcs}</g></svg>` +
    '<span class="wave"><i></i><i></i><i></i><i></i></span>' +
    (text ? `<span>${esc(text)}</span>` : "") +
    // ⭐⭐ Метка скорости — на ВСЕХ кнопках звука (решение владельца 31.07 по живой
    // обкатке: «не отметил второе нажатие, чтобы было видно визуально»). Раньше подпись
    // висела только на крупной кнопке, а на обычной вся разница была в одной дуге значка —
    // то есть невидима. Цифрой, а не словом: она сразу говорит НАСКОЛЬКО медленнее.
    // ⚠ Ставим ПОСЛЕ подписи — `markHeard` меняет первый span кнопки
    // («Прослушать» → «Ещё раз»), и метка не должна попасться ему под руку.
    `<span class="slowtag">${audio.SLOW_RATE}×</span>` +
    "</button>";
}

/** Браузер не пустил автозапуск — говорим это один раз рядом с кнопкой, а не молчим. */
function sndNote(): string {
  return audio.autoplayBlocked() && audio.prefs().autoplay
    ? '<span class="sndnote">звук — по нажатию: так решил браузер</span>'
    : "";
}

/**
 * Пример на экран. Заготовка с пропуском (`examples.text_gapped`) даёт нам ещё и место
 * слова — поэтому мы подсвечиваем именно ту форму, что стоит в предложении
 * (`has _____` → **gone**). В «Контексте» на этом же месте стоит поле ввода.
 */
function exampleHtml(c: Card): string {
  const gapped = c.example_gapped || "";
  const forms = formList(c);
  if (gapped.includes("_____")) {
    const chunks = gapped.split("_____");
    let out = esc(chunks[0]);
    for (let i = 1; i < chunks.length; i++) {
      const form = forms[i - 1] ?? forms[0] ?? c.word;
      out += `<b>${esc(form)}</b>` + esc(chunks[i]);
    }
    return out;
  }
  return esc(c.example?.en || "");
}

/**
 * Варианты для «Выбора» — логика вынесена в `src/choice.ts` (окно М4), здесь остался только
 * сбор ЗАПАСНОЙ колоды: соседи по подходу, сперва той же части речи. Ими добирается хвост,
 * если ядро вариантов не прислало.
 *
 * ⭐ М4: для RU→EN (ступень 2) неверные английские слова теперь приходят готовыми от ядра
 * (`distractors_en`) — из словаря самого человека, а не из случайной колоды подхода.
 */
function choiceOptions(c: Card): ChoiceSpec | null {
  const enRu = c.direction === "en_ru";
  const others = deck.filter((x) => x.word_id !== c.word_id);
  const samePos = others.filter((x) => x.part_of_speech === c.part_of_speech);
  const pool = [...samePos, ...others]
    .map((n) => (enRu ? n.translation : n.word) || "")
    .filter(Boolean);
  return choiceSpec(c, pool);
}

/* ── загрузка подхода ─────────────────────────────────────────────────────── */

function resetCard(): void {
  revealed = false;
  introDone = false;
  wrongPicks = [];
  settled = false;
  sentGrade = null;
  autoGrade = null;
  sending = false;
  sendError = "";
  lastAnswer = null;
  gapText = [];
  gapTries = 0;
  gapHint = "";
  gapOk = null;
  gapMiss = -1;
  picked = [];
  bTries = 0;
  bHint = "";
  bOk = null;
  gPicks = [];
  gHint = "";
  hearRu = false;
  spoken = false;
  listenSeen = false;
  listenProbed = false;
  listenHeard = false;
  hearProbed = false;
  stopClock();           // ⭐ М6: секундомер прошлой карточки не должен тикать на новой
  flStart = 0;
  flMs = null;
  flOut = false;
  audio.stop();          // новая карточка — предыдущее слово не должно догоаривать
  // ⭐⭐ Окно 1 запуска: и чередование скорости начинается заново. Оно живёт ВНУТРИ карточки
  // («не разобрал это слово — переслушай медленно»), тянуть его через занятие незачем.
  audio.resetSpeedCycle();
}

function startDeck(cards: Card[], stats: DailyStats | null): void {
  deck = cards;
  idx = 0;
  startStats = stats;
  tally = { total: 0, easy: 0, hard: 0, again: 0 };
  fluency = { total: 0, fast: 0, timed: 0, sumMs: 0 };   // ⭐ М6: серия считается заново
  earnedAll = [];
  xpRun = { gained: 0, levelUp: 0 };     // ⭐ Ф2.5: опыт подхода считается заново
  pending = [];
  pendingStats = null;
  extraNote = "";
  extraBusy = false;
  resetCard();
  phase = cards.length ? "card" : "empty";
  render("session");
}

async function load(): Promise<void> {
  phase = "loading";
  failure = "";
  render("session");
  try {
    const data = await api.session();
    if (state.me) state.me.today = data.stats;
    startDeck(data.cards, data.stats);
  } catch (e) {
    // ⛔ код ошибки человеку не показываем (правка 26.07): «Ошибка 502» ему нечем помочь.
    failure = api.humanMessage(e, "Связь пропала — занятие не загрузилось");
    phase = "error";
    render("session");
  }
}

/** Вход на экран занятия (зовёт роутер). Незаконченный подход продолжаем, не сбрасываем. */
export function enterSession(): void {
  if (phase === "card" && deck.length) { render("session"); return; }
  void load();
}

/** Уход с экрана: гасим праздничные слои и звук, чтобы ничего не жило над кабинетом. */
export function leaveSession(): void {
  clearFx();
  audio.release();       // отзываем blob-адреса: mp3 не должны оставаться в памяти
  preloadedFor = -1;
  stopClock();           // ⭐ М6: ушли с занятия — секундомер не тикает над кабинетом
  zoomLock(false);       // ушли с занятия — запрет зума не должен пережить экран ввода
}

/* ── отправка оценки в ядро ───────────────────────────────────────────────── */

async function send(grade: 0 | 1 | 2): Promise<void> {
  const card = deck[idx];
  if (!card || sending || sentGrade !== null) return;
  sending = true;
  sendError = "";
  render("session");
  try {
    const res = await api.answer(card.word_id, grade);
    sentGrade = grade;
    lastAnswer = res.state;
    tally.total++;
    if (grade === 2) tally.easy++; else if (grade === 1) tally.hard++; else tally.again++;
    if (state.me) state.me.today = res.stats;
    // ⭐ Ф2.5: опыт и уровень после ответа — как их посчитало ядро. Кабинет и настройки
    // читают это же состояние, поэтому кольцо и маскот обновляются без лишнего запроса.
    if (res.progress && state.me) state.me.xp = res.progress;
    if (res.xp?.gained) {
      xpRun.gained += res.xp.gained;
      if (res.xp.level_up) {
        xpRun.levelUp = res.xp.level ?? xpRun.levelUp;
        praise(`✨ Уровень ${res.xp.level}!`);
      }
    }
    for (const a of res.earned) {
      earnedAll.push({ code: a.code, title: a.title });
      praise(`🏆 ${a.title}`);
    }
  } catch (e) {
    // Человеку — что случилось, а не код шлюза (правка 26.07): «Ошибка 502» ни о чём.
    // Сам факт мы не прячем: ниже стоит кнопка «Отправить ещё раз».
    sendError = api.humanMessage(e, "Связь пропала — ответ не сохранился");
  } finally {
    sending = false;
    render("session");
  }
}

function next(): void {
  idx++;
  resetCard();
  if (idx >= deck.length) { void finish(); return; }
  render("session");
}

/** Конец подхода: спрашиваем ядро, что осталось на сегодня, и показываем сводку. */
async function finish(): Promise<void> {
  phase = "done";
  render("session");
  setTimeout(() => {
    salute();
    praise(tally.again === 0 ? "Отличная работа! 🎉" : "Хорошая работа 💪");
  }, 80);
  try {
    const fresh = await api.session();
    pending = fresh.cards;
    pendingStats = fresh.stats;
    if (state.me) state.me.today = fresh.stats;
  } catch {
    /* сеть мигнула — просто не покажем кнопку «ещё», сводка от этого не врёт */
  }
  render("session");
}

/**
 * «Ещё 5 новых»: просим у ядра надбавку и СРАЗУ начинаем подход из того, что оно дало.
 *
 * ⛔ Своей арифметики здесь нет ни на строку: сколько слов дать, сколько нажатий
 * осталось и хватит ли уже — решает `runner.grant_extra_new`; витрина показывает ответ.
 * Отказ (потолок дня) — не поломка: пишем человеческую строку и оставляем сводку как есть.
 */
async function askExtra(): Promise<void> {
  if (extraBusy) return;
  extraBusy = true;
  extraNote = "";
  render("session");
  try {
    const fresh = await api.sessionExtra();
    if (state.me) state.me.today = fresh.stats;
    clearFx();
    startDeck(fresh.cards, fresh.stats);
  } catch (e) {
    const status = (e as { status?: number }).status ?? 0;
    extraNote = api.humanMessage(e, extra.refusal(status));
    extraBusy = false;
    render("session");
  }
}

/* ── действия пользователя ────────────────────────────────────────────────── */

function reveal(): void {
  if (revealed) return;
  revealed = true;
  render("session");
}

/**
 * ⭐ М1: знакомство закрыто — ТА ЖЕ карточка спрашивает ступенью 1 («Выбор»).
 * Оценка при этом ОДНА: знакомство её не ставит, в `reviews` уедет ответ на проверку.
 * Это тот самый закон конкурентов «первый интервал — секунды, а не следующий день»
 * (Pimsleur 5 с · Memrise ≤ 6 проверок в сессии · Anki `1m`).
 */
function metWord(): void {
  if (introDone) return;
  introDone = true;
  revealed = false;          // проверка начинается с чистого вопроса
  render("session");
}

/**
 * ⭐ М2: «не слышно — показать слово». Ступень 1а опускается до ступени 1 прямо в этой
 * карточке: варианты те же, попытки те же, оценка та же — меняется только опора
 * (услышать → увидеть). Это не «сдался»: ноль за то, что у человека молчат наушники,
 * ставить нечестно, а спуск на ступень ниже — законный ход лестницы (М1).
 */
function showSpelling(): void {
  if (listenSeen || settled) return;
  listenSeen = true;
  render("session");
}

/**
 * ⭐⭐ М5.1 (30.07): «не понял фразу — показать перевод». Та же механика и тот же вид, что
 * у «не слышно — показать слово» на ступени 1а, — и по той же причине: упереться человеку
 * должно быть куда. Замер 30.07: у 71.5% длинных примеров есть слово выше ранга целевого,
 * то есть «не понял фразу» — больше двух третей карточек.
 *
 * ⛔ Ноль за это НЕ ставим и ступень НЕ понижаем (в отличие от 1а, где карточка честно
 * становится ступенью 1): здесь задание остаётся прежним — фраза так же звучит, форму так
 * же надо выбрать. Перевод — опора, а не сдача, поэтому эта функция не трогает ни оценку,
 * ни `settled`, ни `gPicks`. Открытый перевод остаётся до конца карточки.
 */
function showPhraseRu(): void {
  if (hearRu) return;
  hearRu = true;
  render("session");
}

/**
 * Слово прозвучало (нажали кнопку или сработал автозапуск) → подпись единственной кнопки
 * становится «Ещё раз». Правим ПОДПИСЬ В DOM, а не перерисовываем карточку: перерисовка
 * посреди проигрывания сбросила бы состояние кнопки и дёрнула бы звуковую часть кадра.
 */
function markHeard(): void {
  if (listenHeard) return;
  listenHeard = true;
  const btn = document.querySelector<HTMLElement>("button.snd.hear");
  const label = btn?.querySelector<HTMLElement>("span:not(.wave)");
  if (label) label.textContent = "Ещё раз";
  // ⭐ М5: на ступени 1а кнопка играет СЛОВО, на ступени 5 — ФРАЗУ; для незрячего
  // человека это разные вопросы, поэтому подпись берём из адреса самой кнопки.
  const what = btn?.dataset.snd?.startsWith("phrase") ? "фразу" : "слово";
  btn?.setAttribute("aria-label", `Прослушать ${what} ещё раз`);
}

function pick(option: string): void {
  const card = deck[idx];
  if (!card || settled) return;
  const opts = choiceOptions(card);
  if (!opts) return;
  const right = option.toLowerCase() === opts.correct.toLowerCase();

  if (right) {
    settled = true;
    // Оценка выводится из ответа (решение 22.07): верно сразу → 2, со второй попытки → 1.
    const grade: 0 | 1 | 2 = wrongPicks.length === 0 ? 2 : 1;
    autoGrade = grade;
    render("session");
    const el = document.querySelector<HTMLElement>(".opt.correct");
    document.querySelector<HTMLElement>(".qcard")?.classList.add("pop");
    burst(el, 22, 6);
    praise(wrongPicks.length === 0 ? "Верно! 🎉" : "Есть! Со второй попытки 💪");
    void send(grade);
    return;
  }

  wrongPicks.push(option);
  if (wrongPicks.length >= 2) {
    settled = true;                    // две попытки сожжены — показываем правильный
    autoGrade = 0;
    render("session");
    praise("Ничего, вернём это слово сегодня 💡");
    void send(0);
    return;
  }
  render("session");
  const missed = document.querySelector<HTMLElement>(".opt.wrong");
  missed?.classList.add("shake");
  praise("Почти! Ещё разок 💡");
}

/* ── ⭐⭐ М6 «Беглость» (ступень 6): секундомер и ответ ────────────────────────
   Ступень меряет СКОРОСТЬ доступа, поэтому здесь есть две вещи, которых нет больше нигде:
   видимый таймер и оценка, выведенная не из правильности, а из ВРЕМЕНИ. Пороги — в
   `ladder.ts` (`FLUENCY_WINDOW_MS` / `FLUENCY_FAST_MS`), чтобы двигать их одним числом. */

function nowMs(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function stopClock(): void {
  if (!flTimer) return;
  clearInterval(flTimer);
  flTimer = 0;
}

/**
 * Отрисовать секундомер ПРЯМО В DOM, не перерисовывая карточку. Причина та же, по которой
 * так сделана подпись кнопки звука: перерисовка десять раз в секунду сбросила бы состояние
 * кнопок и дёргала бы звуковую часть кадра. Возвращает true, когда время вышло.
 */
function paintClock(): boolean {
  const left = ladder.fluencyLeft(nowMs() - flStart);
  const done = ladder.fluencyExpired(nowMs() - flStart);
  const host = document.querySelector<HTMLElement>(".fltimer");
  if (host) {
    const digits = host.querySelector<HTMLElement>(".flnum");
    if (digits) digits.textContent = String(left);
    const bar = host.querySelector<HTMLElement>(".flbar i");
    const pct = Math.max(0, Math.min(100,
      100 - ((nowMs() - flStart) / ladder.FLUENCY_WINDOW_MS) * 100));
    if (bar) bar.style.width = `${pct}%`;
    // Последняя секунда — предупреждаем цветом: человек должен видеть, что время уходит.
    host.classList.toggle("hot", left <= 1);
  }
  return done;
}

/** Запустить/остановить отсчёт под текущую карточку. Зовётся из каждой отрисовки кадра. */
function fluencyClock(c: Card): void {
  if (shownMode(c) !== "fluency" || settled) { stopClock(); return; }
  if (!flStart) flStart = nowMs();
  paintClock();
  if (flTimer) return;
  flTimer = window.setInterval(() => {
    const cur = deck[idx];
    if (!cur || shownMode(cur) !== "fluency" || settled) { stopClock(); return; }
    if (paintClock()) fluencyTimeout();
  }, 100);
}

/** Общий финал ступени 6: оценка уже известна, дальше — как у любого автоответа. */
function fluencyDone(ms: number | null): void {
  stopClock();
  settled = true;
  flMs = ms;
  const g = ladder.fluencyGrade(ms);
  autoGrade = g;
  // ⭐ Пункт 2: результат серии считает витрина по своему же заходу. ⛔ В БД не уезжает.
  fluency.total++;
  if (ms !== null && !ladder.fluencyExpired(ms)) { fluency.timed++; fluency.sumMs += ms; }
  if (g === 2) fluency.fast++;
  render("session");
  if (g === 2) {
    document.querySelector<HTMLElement>(".qcard")?.classList.add("pop");
    burst(document.querySelector<HTMLElement>(".fltimer"), 18, 5);
  }
  praise(g === 2 ? "Бегло! 🎉" : g === 1 ? "Есть, но небыстро 💪" : "Ничего, вернём это слово 💡");
  // ⭐ Обычный POST /answer, как у всех ступеней: на боксе 6 оценки 2 и 1 дают ОДНО
  // состояние (box 6, 90 дней), поэтому Leitner беглость не двигает САМА — обходить
  // ядро не надо. А ноль это не «беглость понизила бокс», а забытое слово.
  void send(g);
}

/** Нажал «помню» / «не помню». Время берём в момент нажатия, а не в момент отрисовки. */
function fluencyAnswer(remember: boolean): void {
  const c = deck[idx];
  if (!c || settled) return;
  fluencyDone(remember ? nowMs() - flStart : null);
}

/** Время вышло: карточка закрывается сама, оценка 0 — слово не достали. */
function fluencyTimeout(): void {
  if (settled) return;
  flOut = true;
  fluencyDone(null);
}

/* ── ⭐ М3 «Пропуск» (ступень 3) и ⭐ М5 «Фраза на слух» (ступень 5) ────────── */

/**
 * Какой пропуск сейчас на экране. Ступени 3 и 5 отвечают ОДНИМИ И ТЕМИ ЖЕ кнопками
 * (`data-gopt`), поэтому и обработчик у них один: разница между ними — в вопросе и в
 * материале, а не в способе ответа. Спрашиваем показываемый вид, а не бокс: так
 * обработчик не разъедется с отрисовкой ни на одном спуске ступенью ниже.
 */
function activeGap(c: Card): GapSpecChoice | null {
  return shownMode(c) === "hear" ? hearFor(c) : gapFor(c);
}

/**
 * Нажали кнопку под пропуском. Оценка — общий закон (решение 22.07): верно сразу → 2,
 * со второй попытки → 1, два промаха → 0.
 *
 * ⛔ К форме придираемся так же жёстко, как в «Контексте»: `has _____` + `go` НЕ засчитан.
 * Разница только в способе ответа — кнопка вместо клавиатуры, потому что печатать на этой
 * ступени рано (продукция раньше узнавания мешает — Barcroft).
 */
function pickGap(option: string): void {
  const c = deck[idx];
  const spec = c ? activeGap(c) : null;
  if (!c || !spec || settled) return;

  if (checkChoice(option, spec)) {
    settled = true;
    gHint = "";
    const g = gradeFor(gPicks.length);          // общий закон оценки: 2 / 1 / 0
    autoGrade = g;
    render("session");
    document.querySelector<HTMLElement>(".qcard")?.classList.add("pop");
    burst(document.querySelector<HTMLElement>(".gapslot"), 22, 6);
    praise(gPicks.length === 0 ? "Верно! 🎉" : "Есть! Со второй попытки 💪");
    void send(g);
    return;
  }

  gPicks.push(option);
  if (gPicks.length >= 2) {
    settled = true;
    autoGrade = 0;
    gHint = "";
    render("session");
    praise("Ничего, вернём это слово сегодня 💡");
    void send(0);
    return;
  }
  // Первая осечка: говорим, ЧТО не так. «Слово верное — форма другая» человек должен
  // услышать отдельно от «не то слово» — ради этого различия ступень и делалась.
  // ⭐⭐ Окно 1 запуска: у оборота на кнопках стоят ДРУГИЕ ОБОРОТЫ, а не его формы —
  // значит и промах там всегда смысловой, разбор «форма не та» был бы неправдой.
  const formMiss = spec.source !== "function" && spec.source !== "construction"
                   && isFormMiss(option, c);
  // ⭐⭐ М5.1: на ступени 5 смысловой промах САМ открывает перевод фразы — вторую попытку
  // человек не должен тратить вслепую. ⛔ А промах по ФОРМЕ перевода не открывает: там он
  // врёт («Что ты сказал…» тянет к `said`, хотя после `did` идёт `say`). Правило —
  // `gap.opensRuAfterMiss`, чтобы его можно было проверить запуском.
  const hearing = shownMode(c) === "hear";
  if (opensRuAfterMiss(hearing, formMiss)) hearRu = true;
  gHint = formMiss
    ? "<b>Слово верное — форма другая.</b> Посмотри, чего требует предложение."
    : hearing
      ? "<b>Не то слово.</b> Показали перевод фразы — теперь видно, что вставлять."
      : "<b>Не то слово.</b> Перечитай перевод — он и есть задание.";
  render("session");
  praise(formMiss ? "Форма не та 💡" : "Почти! Ещё разок 💡");
  document.querySelector<HTMLElement>(".opt.wrong")?.classList.add("shake");
}

/* ── «Контекст»: сверка по форме ───────────────────────────────────────────── */

/** Что сказать после промаха. Тип промаха разбирает `grammar.check`. */
function missHint(kind: "form" | "typo" | "miss", expected: string): string {
  if (kind === "form") {
    return "<b>Слово верное — форма другая.</b> Посмотри, чего требует предложение: " +
      `подсказка ${esc(mask(expected))}`;
  }
  if (kind === "typo") return `<b>Почти!</b> Проверь написание: ${esc(mask(expected))}`;
  return `<b>Не то слово.</b> Подсказка: ${esc(mask(expected))}`;
}

/** Итоговая строка, когда ответ так и не сошёлся: правильный ответ + что было набрано. */
function answerHint(expected: string[], given: string[]): string {
  const right = expected.join(" ");
  const mine = given.filter((s) => s.trim()).join(" ");
  return `Правильно: <b>${esc(right)}</b>` +
    (mine && mine.toLowerCase() !== right.toLowerCase() ? ` · ты написал «${esc(mine)}»` : "");
}

/**
 * Сверка ответа. Оценка выводится так же, как в «Выборе» (решение 22.07):
 * верно сразу → 2, верно со второй попытки → 1, два промаха → 0.
 * ⛔ К форме придираемся жёстко — ради этого режим и делался.
 */
function checkGap(): void {
  const c = deck[idx];
  const spec = c ? gapSpec(c) : null;
  if (!c || !spec || settled) return;

  const answers = spec.targets.map((_, i) => (gapText[i] || "").trim());
  if (answers.some((a) => !a)) { focusGap(true); return; }   // пусто — не сверяем, а просим дописать

  const verdict = check(answers, spec, c);
  if (verdict.ok) {
    settled = true;
    gapOk = true;
    gapHint = "";
    const grade: 0 | 1 | 2 = gapTries === 0 ? 2 : 1;
    autoGrade = grade;
    render("session");
    document.querySelector<HTMLElement>(".qcard")?.classList.add("pop");
    burst(document.querySelector<HTMLElement>(".gapin.ok"), 22, 6);
    praise(gapTries === 0 ? "Верно! 🎉" : "Есть! Со второй попытки 💪");
    void send(grade);
    return;
  }

  gapTries++;
  if (gapTries >= 2) {
    settled = true;
    gapOk = false;
    autoGrade = 0;
    gapHint = answerHint(spec.targets, answers);
    render("session");
    praise("Ничего, вернём это слово сегодня 💡");
    void send(0);
    return;
  }

  // Первая осечка: подсказка по типу промаха и вторая попытка.
  gapHint = missHint(verdict.kind, verdict.expected);
  gapMiss = verdict.gap;
  // ⭐ 26.07 (решение владельца): не угадал — поля чистятся САМИ, стирать руками не надо.
  // ⭐ Уточнение по обкатке: чистим ВЕСЬ ответ, а не только промахнувшееся место. Разрывной
  // оборот человек вводит как ОДНО целое («bring about»), поэтому и сбрасывать его надо
  // целиком — иначе половина стёрлась, половина осталась, и непонятно, что переписывать.
  gapText = spec.targets.map(() => "");
  render("session");
  praise(verdict.kind === "form" ? "Форма не та 💡" : "Почти! Ещё разок 💡");
  const el = document.querySelector<HTMLElement>(`.gapin[data-gap="${verdict.gap}"]`);
  el?.classList.add("shake");
  focusGap();
}

/** «Не знаю — показать»: это осознанный промах, поэтому оценка 0 (слово вернётся сегодня). */
function giveUpGap(): void {
  const c = deck[idx];
  const spec = c ? gapSpec(c) : null;
  if (!c || !spec || settled) return;
  settled = true;
  gapOk = false;
  autoGrade = 0;
  gapHint = answerHint(spec.targets, spec.targets.map((_, i) => (gapText[i] || "")));
  render("session");
  void send(0);
}

/* ── «Собери фразу»: кнопки-слова ─────────────────────────────────────────── */

/** Взять слово из набора в строку сборки (номер = место в наборе, а не текст). */
function putWord(i: number): void {
  if (settled || picked.includes(i)) return;
  picked.push(i);
  // ⭐ Правка по обкатке 27.07: разбор промаха («Использованы не все нужные слова») ГАСНЕТ
  // на первом же касании. Прежнее решение «пусть висит перед глазами» на телефоне читалось
  // как вердикт на ТЕКУЩУЮ сборку — человек собирал заново и видел приговор ещё не начатой
  // попытке. Начал собирать — прошлый разбор больше не про эту строку.
  bHint = "";
  render("session");
}

/** Вернуть слово обратно в набор (нажал по уже выложенному). */
function takeWord(pos: number): void {
  if (settled || pos < 0 || pos >= picked.length) return;
  picked.splice(pos, 1);
  bHint = "";                 // то же правило: тронул сборку — разбор прошлой попытки снят
  render("session");
}

/** Что сказать после промаха: человеку важно ЧТО не так — форма, порядок или лишнее. */
function buildHint(kind: "form" | "order" | "extra" | "short"): string {
  if (kind === "form") {
    return "<b>Слово верное — форма другая.</b> Посмотри, чего требует предложение.";
  }
  if (kind === "order") return "<b>Слова все нужные — порядок не тот.</b>";
  if (kind === "short") return "Использованы не все нужные слова — собери предложение целиком.";
  return "<b>Есть лишнее слово.</b> В наборе нарочно лежат слова, которые не нужны.";
}

/**
 * Сверка сборки. Оценка выводится так же, как в «Выборе» и «Контексте»
 * (`build.gradeFor`): собрал сразу → 2, со второй попытки → 1, не собрал → 0.
 * ⚠ Недособранное предложение попытку НЕ жжёт: сколько слов нужно, человек заранее не
 * знает — среди кнопок есть лишние. Это тот же приём, что пустое поле в «Контексте».
 */
function checkPhrase(): void {
  const c = deck[idx];
  const spec = c ? buildFor(c) : null;
  if (!c || !spec || settled) return;
  const verdict = checkBuild(picked.map((i) => spec.bank[i]), spec, c);

  if (verdict.ok) {
    settled = true;
    bOk = true;
    bHint = "";
    const g = gradeFor(bTries);
    autoGrade = g;
    render("session");
    document.querySelector<HTMLElement>(".qcard")?.classList.add("pop");
    burst(document.querySelector<HTMLElement>(".bline"), 22, 6);
    praise(bTries === 0 ? "Верно! 🎉" : "Есть! Со второй попытки 💪");
    void send(g);
    return;
  }

  if (verdict.kind === "short") {
    bHint = buildHint("short");
    render("session");
    document.querySelector<HTMLElement>(".bline")?.classList.add("shake");
    return;
  }

  bTries++;
  if (bTries >= 2) {
    settled = true;
    bOk = false;
    autoGrade = 0;
    bHint = "";
    render("session");
    praise("Ничего, вернём это слово сегодня 💡");
    void send(0);
    return;
  }

  // Первая осечка: разбор промаха и вторая попытка. ⭐ Сборку сбрасываем ЦЕЛИКОМ — тот же
  // закон, что у полей «Контекста» (26.07): предложение собирают как одно целое, значит и
  // начинать заново надо с чистой строки, а не разбирать чужую половину.
  bHint = buildHint(verdict.kind);
  picked = [];
  render("session");
  praise(verdict.kind === "form" ? "Форма не та 💡"
       : verdict.kind === "order" ? "Порядок не тот 💡" : "Почти! Ещё разок 💡");
  document.querySelector<HTMLElement>(".bline")?.classList.add("shake");
}

/** «не знаю — показать»: осознанный промах, поэтому 0 (слово вернётся сегодня). */
function giveUpPhrase(): void {
  const c = deck[idx];
  const spec = c ? buildFor(c) : null;
  if (!c || !spec || settled) return;
  settled = true;
  bOk = false;
  bHint = "";
  autoGrade = 0;
  render("session");
  void send(0);
}

function grade(g: 0 | 1 | 2): void {
  if (sentGrade !== null) { next(); return; }
  if (g === 2) {
    document.querySelector<HTMLElement>(".qcard")?.classList.add("pop");
    burst(null, 16, 5);
  }
  void send(g).then(() => { if (sentGrade !== null) next(); });
}

/* ── отрисовка ────────────────────────────────────────────────────────────── */

function head(): string {
  const total = deck.length || 1;
  const pct = Math.round((idx / total) * 100);
  return (
    '<div class="sess-top">' +
      '<button class="x" type="button" data-go="cabinet" aria-label="Выйти из занятия">×</button>' +
      `<div class="sprog"><i style="width:${pct}%"></i></div>` +
      `<div class="n">${Math.min(idx + 1, deck.length)} / ${deck.length}</div>` +
    "</div>"
  );
}

/**
 * Вопрос карточки. «Тишина» = только слово: ни транскрипции, ни бейджей —
 * всё это переезжает в раскрытый ответ, иначе «ничего лишнего» не выполняется.
 *
 * Звук (Ф-D) живёт здесь, пока вопрос АНГЛИЙСКИЙ: «Тишина» — волной под словом (Т2),
 * «Досье» и «Выбор» — иконкой в строке с транскрипцией (Д1). Вопрос по-русски — кнопки
 * тут нет вообще: она выдала бы ответ (⭐ закон), она появится в раскрытом ответе.
 */
function askBlock(c: Card, m: Mode, sound: string | null): string {
  const enRu = c.direction === "en_ru";
  const prompt = enRu ? c.word : (c.translation || c.word);
  const here = sound && enRu ? sound : null;      // у русского вопроса места для звука нет
  let html = `<div class="qword${enRu ? "" : " ru"}">${esc(prompt)}</div>`;

  if (enRu && c.ipa && m !== "silence") {
    // Д1: запись и звук — одна мысль, поэтому одна строка.
    html += here
      ? `<div class="qipa sndrow">${esc(c.ipa)}${snd(here, "plain", "Прослушать слово")}</div>`
      : `<div class="qipa">${esc(c.ipa)}</div>`;
  } else if (here) {
    // Т2 (и «Досье» без транскрипции): круглая кнопка-волна под словом.
    html += `<div class="sndrow under">${snd(here, "round", "Прослушать слово")}</div>`;
  }
  if (here) html += sndNote();

  if (m === "dossier") {
    const pos = c.part_of_speech ? (POS_RU[c.part_of_speech.toLowerCase()] || c.part_of_speech) : "";
    const badges = [
      pos ? `<span class="badge pos">${esc(pos)}</span>` : "",
      c.verb_badge ? `<span class="badge verb">${esc(c.verb_badge)}</span>` : "",
    ].join("");
    if (badges) html += `<div class="qbadges">${badges}</div>`;
  }
  return html;
}

/**
 * ⭐ ОКНО М2 · вопрос ступени 1а «На слух»: звучит слово, ТЕКСТА НЕТ.
 *
 * Почему тут можно то, чего нельзя нигде: обычный закон звука («кнопка только когда
 * английское слово уже на экране») защищает от подсказки — в «Выборе» кнопка озвучила
 * бы ответ. Здесь ответ — РУССКИЙ перевод, его человек и выбирает, а звук выдаёт ровно
 * то, что и должен: сам вопрос. Поэтому порядок обратный — звук есть, написания нет.
 *
 * Написание появляется в двух случаях, и оба честные:
 *   • ответ дан — тогда это уже разбор (слово · транскрипция · перевод · пример);
 *   • человек нажал «не слышно — показать слово»: карточка молча опускается на ступень
 *     ниже и превращается в обычный «Выбор» с теми же вариантами. ⛔ Оставить человека
 *     перед четырьмя кнопками без вопроса нельзя — а причин не услышать хватает
 *     (наушники не подключились, тихий режим телефона, WebView не пустил звук).
 */
function listenAsk(c: Card, pl: audio.Places): string {
  // Ответ дан или человек попросил написание → показываем ровно то же, что «Выбор»:
  // слово, транскрипция, кнопка звука рядом (Д1).
  if (settled || listenSeen) return askBlock(c, "choice", pl.word);

  const r = pl.word;
  let html = '<div class="qhead">Послушай слово</div>';
  // ⭐ Обкатка 28.07: кнопка ОДНА. Их было две («Прослушать» + маленькая «ещё раз»), но
  // обе слали один и тот же адрес — разница была чисто технической (класс `hear`, на него
  // завязаны пробел и Enter на ПК). Вторая кнопка ничего не давала и путала: человек искал,
  // чем они отличаются. Теперь у единственной кнопки меняется ПОДПИСЬ — после первого
  // проигрывания она честно говорит «Ещё раз».
  html += '<div class="listen">' +
    (r
      ? snd(r, "big", listenHeard ? "Прослушать ещё раз" : "Прослушать слово",
            listenHeard ? "Ещё раз" : "Прослушать", "hear")
      : '<span class="bph">звук не доехал — нажми «показать слово»</span>') +
    "</div>";
  if (r) html += sndNote();
  html += '<div class="listenskip">' +
    '<button class="linkbtn" type="button" data-act="hearskip">не слышно — показать слово</button>' +
    "</div>";
  return html;
}

/**
 * Блок ответа с подписью. Правка 26.07 по живой обкатке: раньше формы, коллокации и
 * заметка лежали в одинаковом оформлении и читались одной кашей — а это РАЗНЫЕ сущности.
 * Теперь у каждой своя подпись и своя черта сверху, и они не путаются друг с другом.
 */
function labeled(title: string, body: string): string {
  return body ? `<div class="qblock"><div class="qblabel">${esc(title)}</div>${body}</div>` : "";
}

/** «С чем ходит» — коллокации (их в базе ровно 3 на слово). */
function collocBlock(c: Card): string {
  if (!c.collocations.length) return "";
  return labeled("С чем ходит", '<div class="qcolloc">' +
    c.collocations.slice(0, 3).map((x) => `<span>${esc(x)}</span>`).join("") + "</div>");
}

/**
 * «Как употребляется» — заметка об ОГРАНИЧЕНИИ употребления («британский разговорный;
 * зайти к кому-то домой»). Единственное поле, которое не даёт сказать
 * `I'll call round to the office`, — поэтому не убираем, а подписываем.
 */
function noteBlock(c: Card): string {
  return c.context_note ? labeled("Как употребляется", `<p class="qnote">${esc(c.context_note)}</p>`) : "";
}

/**
 * ⭐⭐ РЕГИСТР — главная строка карточки тематического набора (21.08).
 * Одна строка: точки силы · при ком так говорят. Ниже — ОДНА мягкая замена (только
 * при силе 2–3) и ⚠ ловушка, если она есть. ⛔ Ни морали, ни «так говорить нельзя»:
 * человеку сказано, при ком уместно, а решает он сам.
 */
function registerBlock(c: Card): string {
  const line = registerLine(c.register as any);
  if (!line) return "";
  const soft = softerLine(c.register as any);
  const note = noteLine(c.register as any);
  return labeled("Как это звучит",
    `<p class="qreg">${esc(line)}</p>` +
    (soft ? `<p class="qregsoft">${esc(soft)}</p>` : "") +
    (note ? `<p class="qnote">⚠ ${esc(note)}</p>` : ""));
}

/** Кнопка «Фраза» у раскрытого примера (правка 26.07: везде, а не только в «Контексте»). */
function phraseBtn(refPhrase: string | null): string {
  return refPhrase
    ? `<div class="sndrow exsnd">${snd(refPhrase, "pill", "Прослушать фразу", "Фраза")}</div>`
    : "";
}

/**
 * ⭐ М3: ДЛИННЫЙ ПРИМЕР ВТОРЫМ В РАЗБОРЕ — «а живьём это звучит так».
 *
 * Вторая ось лестницы (методика, разд. 3.3): работаем на коротком и знакомом, а живой
 * язык видим задолго до того, как с него спросят — за раз меняется ОДНА вещь. Кому
 * показывать, решает ЯДРО: поле `example_long` приезжает только с бокса 2 и только когда
 * рабочий пример короткий. Здесь мы ничего не выбираем — рисуем то, что дали.
 *
 * `phraseSound` — можно ли ставить кнопку «Фраза» (пример раскрыт и звук фраз включён).
 */
function longExampleBlock(c: Card, phraseSound: boolean): string {
  const ex = c.example_long;
  if (!ex?.en) return "";
  const ref = phraseSound && c.example_long_id ? audio.ref("phrase", c.example_long_id) : null;
  return labeled("А живьём это звучит так",
    '<div class="qex longex">' +
      `<div class="en">${esc(ex.en)}</div>` +
      (ex.ru ? `<div class="ru">${esc(ex.ru)}</div>` : "") +
      phraseBtn(ref) +
    "</div>");
}

/**
 * Раскрытый ответ. Что именно показать, решает режим:
 *   extras  — коллокации и заметка («Досье», «Выбор», «Контекст»); у «Тишины» их нет;
 *   example — предложение (у «Контекста» оно уже стоит выше с вписанным словом);
 *   ipa     — транскрипция в ответе (нужна «Тишине»: в вопросе её не было);
 *   sound   — адрес озвучки СЛОВА: приходит только у русского вопроса (RU→EN), где до
 *             раскрытия кнопки быть не могло — теперь английское слово на экране (⭐ закон);
 *   phrase  — адрес озвучки ПРИМЕРА: пример раскрыт, значит кнопка ничего не выдаёт.
 */
function revealBlock(c: Card, o: { extras?: boolean; example?: boolean; ipa?: boolean;
                                   sound?: string | null; phrase?: string | null } = {}): string {
  const enRu = c.direction === "en_ru";
  const ipaTag = c.ipa ? ` <span class="ipa">${esc(c.ipa)}</span>` : "";
  const answer = enRu
    ? esc(c.translation || "—") + (o.ipa ? ipaTag : "")
    : esc(c.word) + ipaTag;
  let html = '<div class="reveal">' +
    (!enRu && o.sound
      ? `<div class="qtrans small sndrow">${answer}${snd(o.sound, "plain", "Прослушать слово")}</div>`
      : `<div class="qtrans${enRu ? "" : " small"}">${answer}</div>`);
  if (enRu && c.translations_all.length) {
    html += `<div class="qalt">${esc(c.translations_all.slice(0, 3).join(" · "))}</div>`;
  }
  if (o.example !== false && c.example) {
    html += '<div class="qex">' +
      `<div class="en">${exampleHtml(c)}</div>` +
      (c.example.ru ? `<div class="ru">${esc(c.example.ru)}</div>` : "") +
      phraseBtn(o.phrase ?? null) +
      "</div>";
    html += longExampleBlock(c, !!o.phrase);      // ⭐ М3: живой пример вторым, с бокса 2
  }
  if (o.extras) html += collocBlock(c) + registerBlock(c) + noteBlock(c);
  return html + "</div>";
}

/**
 * Формы слова из `lexicon.forms` — грамматический итог «Контекста».
 * ⭐ Правка 26.07: показываем ТОЛЬКО у нерегулярных (`go` → `went`/`gone`). У регулярных
 * (`called round` / `calling round` — это 514 слов из 687, 75%) учить нечего, а блок
 * плашек создаёт кашу. Правило считает `grammar.hasIrregularForms` на готовых данных.
 */
function formsBlock(c: Card): string {
  if (!hasIrregularForms(c)) return "";
  const rows = Object.entries(c.forms || {})
    .filter(([, v]) => !isBlankForm(v))          // прочерк «—» вместо формы — не знание
    .map(([k, v]) => `<span><i>${esc(FORM_LABEL[k] || k)}</i> ${esc(v)}</span>`);
  return rows.length ? labeled("Формы слова", `<div class="qforms">${rows.join("")}</div>`) : "";
}

/**
 * Тело «Контекста»: предложение, в котором пропуск — настоящее поле ввода.
 * ⭐ Сюда попадает ТОЛЬКО окрепшее слово (бокс 2+, вопрос по-русски): человек достаёт
 * слово из головы сам и ставит в нужную форму — это продукция. Подсказки-леммы больше
 * нет и быть не может: она превращала задание в переписывание, а новому слову этот
 * режим теперь не даётся вовсе (закон в `shownMode`).
 */
function contextBody(c: Card, spec: GapSpec, pl: audio.Places): string {
  const many = spec.targets.length > 1;
  const input = (g: number): string => {
    const typed = gapText[g] || "";
    if (settled) {
      // В итоге в предложении стоит КАНОНИЧЕСКАЯ форма (а не «BROKE» как набрали) —
      // человек должен увидеть верное написание; что набрал он, скажет подсказка.
      return `<input class="gapin ${gapOk ? "ok" : "no"}${many ? " short" : ""}" ` +
        `value="${esc(spec.targets[g])}" disabled aria-label="пропуск ${g + 1}">`;
    }
    return `<input class="gapin${many ? " short" : ""}${g === gapMiss ? " miss" : ""}" ` +
      `data-gap="${g}" type="text" ` +
      `value="${esc(typed)}" inputmode="text" autocapitalize="off" autocomplete="off" ` +
      `autocorrect="off" spellcheck="false" enterkeyhint="done" ` +
      `placeholder="${many ? "" : "слово"}" aria-label="пропущенное слово ${g + 1}">`;
  };

  let sentence: string;
  if (!many) {
    // Один пропуск — обычный поток текста: поле переносится вместе со словами.
    sentence = esc(spec.chunks[0]);
    for (let i = 1; i < spec.chunks.length; i++) sentence += input(i - 1) + esc(spec.chunks[i]);
  } else {
    // ⭐ Правка 26.07: разрывной оборот (2–3 пропуска, 69 карточек) на телефоне разъезжался —
    // поля вставали как попало между обтекающим текстом. Собираем предложение рядом
    // выровненных кусков: слово = кусок, а СОСЕДНИЕ поля (между ними нет текста —
    // `was _____ _____ during`) склеены в один неразрывный блок и никогда не расходятся
    // по разным строкам. Перенос идёт по границам слов, как и раньше.
    const items: string[] = [];
    let pair: string[] = [];
    const flushPair = (): void => {
      if (!pair.length) return;
      items.push(`<span class="pair">${pair.join("")}</span>`);
      pair = [];
    };
    const words = (text: string): void => {
      for (const w of text.trim().split(/\s+/)) if (w) items.push(`<span class="txt">${esc(w)}</span>`);
    };
    words(spec.chunks[0]);
    for (let i = 1; i < spec.chunks.length; i++) {
      pair.push(input(i - 1));
      const tail = spec.chunks[i].trim();
      if (tail) { flushPair(); words(tail); }   // есть текст → пара закончилась
    }
    flushPair();
    sentence = items.join("");
  }

  let html = '<div class="qhead">Впиши пропущенное слово</div>';
  html += '<div class="qex ctx">' +
    `<div class="en${many ? " multi" : ""}">${sentence}</div>` +
    (c.example?.ru ? `<div class="ru">${esc(c.example.ru)}</div>` : "") +
    "</div>";
  if (gapHint) html += `<div class="gaphint${settled && !gapOk ? " bad" : ""}">${gapHint}</div>`;

  if (settled) {
    // К1: сверка прошла — можно и слово, и фразу целиком, ничего не выдавая заранее.
    const sounds = pl.word || pl.phrase
      ? '<div class="sndrow ctxsnd">' +
        (pl.word ? snd(pl.word, "round", "Прослушать слово") : "") +
        (pl.phrase ? snd(pl.phrase, "pill", "Прослушать фразу", "Фраза") : "") +
        "</div>"
      : "";
    html += '<div class="reveal">' +
      `<div class="qtrans small">${esc(c.word)}` +
      (c.ipa ? ` <span class="ipa">${esc(c.ipa)}</span>` : "") + "</div>" +
      (c.translation ? `<div class="qalt">${esc(c.translation)}</div>` : "") +
      sounds +
      longExampleBlock(c, !!pl.phrase) +
      formsBlock(c) +
      collocBlock(c) +
      registerBlock(c) + noteBlock(c) +
      "</div>";
  } else {
    html += '<div class="qtap ctxact">' +
      '<button class="tapbtn" type="button" data-act="check">Проверить</button>' +
      '<button class="linkbtn" type="button" data-act="idk">не знаю — показать</button>' +
      "</div>";
  }
  return html;
}

/**
 * Тело «Собери фразу»: русское предложение — задание, английское собирается кнопками.
 *
 * ⭐ Новому слову (вопрос EN→RU) сверху стоит ДОЗА: лемма, транскрипция и перевод. Это и
 * есть «материал до спроса» — из леммы `go` человек выводит нужную форму `gone`, а не
 * угадывает слово, которого никогда не видел. Окрепшему слову дозы нет: оно должно
 * вспоминаться само, а среди кнопок его ещё надо узнать.
 *
 * Звук — по общему ⭐ закону: у нового слова английское слово уже на экране (доза), поэтому
 * кнопка стоит там же, где в «Досье»; у окрепшего до разбора её нет вовсе. Фраза — только
 * после сверки: в ней стоит собранный ответ.
 */
function buildBody(c: Card, spec: BuildSpec, pl: audio.Places): string {
  const enRu = c.direction === "en_ru";
  let html = '<div class="qhead">Собери предложение из слов</div>';

  if (enRu) {
    html += '<div class="bdose">' +
      `<span class="w">${esc(c.word)}</span>` +
      (c.ipa ? `<span class="ipa">${esc(c.ipa)}</span>` : "") +
      (pl.word ? snd(pl.word, "plain", "Прослушать слово") : "") +
      (c.translation ? `<div class="t">${esc(c.translation)}</div>` : "") +
      "</div>";
    if (pl.word) html += sndNote();
  }

  html += `<div class="bgoal">${esc(spec.ru)}</div>`;

  if (!settled) {
    // Строка сборки: нажатие по выложенному слову возвращает его в набор.
    const line = picked.length
      ? picked.map((b, pos) =>
          `<button class="btok" type="button" data-take="${pos}">${esc(spec.bank[b])}</button>`).join("")
      : '<span class="bph">нажимай слова — они встанут сюда</span>';
    html += `<div class="bline">${line}</div>`;
    // Набор: выложенные кнопки остаются на месте невидимыми, чтобы набор не прыгал под пальцем.
    html += '<div class="bank">' + spec.bank.map((w, i) =>
      `<button class="btok bnk${picked.includes(i) ? " used" : ""}" type="button" ` +
      `data-put="${i}"${picked.includes(i) ? " disabled" : ""}>${esc(w)}</button>`).join("") + "</div>";
    if (bHint) html += `<div class="gaphint">${bHint}</div>`;
    html += '<div class="qtap ctxact">' +
      `<button class="tapbtn" type="button" data-act="bcheck"${picked.length ? "" : " disabled"}>Проверить</button>` +
      '<button class="linkbtn" type="button" data-act="bidk">не знаю — показать</button>' +
      "</div>";
    return html;
  }

  // Исход: правильное предложение целиком, изучаемое слово выделено.
  const sentence = spec.tokens.map((w, i) =>
    spec.targets.includes(i) ? `<b>${esc(w)}</b>` : esc(w)).join(" ");
  html += `<div class="qex ctx${bOk ? " ok" : " no"}"><div class="en">${sentence}</div></div>`;

  const sounds = pl.word || pl.phrase
    ? '<div class="sndrow ctxsnd">' +
      (pl.word ? snd(pl.word, "round", "Прослушать слово") : "") +
      (pl.phrase ? snd(pl.phrase, "pill", "Прослушать фразу", "Фраза") : "") +
      "</div>"
    : "";
  // ⚠ У нового слова лемма с переводом уже стоит дозой наверху — второй раз её не печатаем
  // (тот же урок, что в окне 1: экран ответа не должен читаться кашей).
  html += '<div class="reveal">' +
    (enRu ? "" :
      `<div class="qtrans small">${esc(c.word)}` +
      (c.ipa ? ` <span class="ipa">${esc(c.ipa)}</span>` : "") + "</div>" +
      (c.translation ? `<div class="qalt">${esc(c.translation)}</div>` : "")) +
    sounds + longExampleBlock(c, !!pl.phrase) + formsBlock(c) + collocBlock(c) + registerBlock(c) + noteBlock(c) +
    "</div>";
  return html;
}

/**
 * ⭐⭐ ТЕЛО СТУПЕНИ 3 «ПРОПУСК ВЫБОРОМ» (окно М3).
 *
 * Короткий пример с пропуском + его перевод (перевод и есть задание: он говорит, ЧТО
 * должно стоять в пропуске), под ними 3–4 кнопки. Клавиатуры нет нарочно: печатать —
 * это ступень 4, а здесь мы проверяем узнавание в контексте и ФОРМУ.
 *
 * ⭐ Служебному слову (`would`) кнопками идут другие служебные слова — это и есть
 * «спрашиваем конструкцией, а не переводом»: «_____ you like some tea?» → would.
 *
 * Звук молчит до ответа по общему ⭐ закону: слово стоит на одной из кнопок.
 *
 * ⭐⭐ `hearing = true` — ЭТО ЖЕ ТЕЛО РАБОТАЕТ СТУПЕНЬЮ 5 «фраза на слух» (окно М5).
 * Своего экрана она не получила нарочно: задание у ступеней 3 и 5 одно и то же (один
 * пропуск, ответ кнопкой, сверка по форме), разница ровно в двух вещах — предложение
 * ЗВУЧИТ целиком (крупная кнопка сверху) и перевод придержан до разбора. Материал при
 * этом длинный, но его подменяет ядро само: короткий пример живёт до бокса 3.
 *
 * ⭐⭐ М5.1 (30.07, по обкатке владельца): придержан — не значит «недоступен». Под
 * заданием стоит кнопка «не понял фразу — показать перевод»: нажал → перевод появился и
 * остался до конца карточки, ноль за это не ставится и ступень не понижается. Иначе
 * человеку некуда упереться — у 71.5% длинных примеров есть незнакомое слово.
 */
function gapBody(c: Card, spec: GapSpecChoice, pl: audio.Places, hearing = false): string {
  const head = hearing
    ? "Послушай фразу и выбери, что стоит в пропуске"
    : spec.source === "function"
      ? "Что стоит в пропуске?"
      // ⭐⭐ Окно 1 запуска: у оборота на кнопках стоят другие обороты — значит спрашиваем
      // СМЫСЛ, и подпись обязана это назвать («нужную форму» здесь было бы враньём).
      : spec.source === "construction"
        ? "Какой оборот подходит по смыслу?"
        : spec.source === "forms" ? "Выбери нужную форму" : "Выбери подходящее слово";
  let html = `<div class="qhead">${head}</div>`;

  // ⭐ М5: звук — САМ ВОПРОС, поэтому кнопка крупная и стоит первой, как на ступени 1а.
  // После ответа она отсюда уходит: фраза звучит уже из разбора, вместе со словом.
  if (hearing && !settled) {
    html += '<div class="listen phrase">' +
      (pl.phrase
        ? snd(pl.phrase, "big", listenHeard ? "Прослушать фразу ещё раз" : "Прослушать фразу",
              listenHeard ? "Ещё раз" : "Прослушать", "hear")
        : '<span class="bph">звук не доехал — читай глазами</span>') +
      "</div>";
    if (pl.phrase) html += sndNote();
  }

  const slot = settled
    ? `<b class="gapslot done">${esc(spec.correct)}</b>`
    : '<span class="gapslot">_____</span>';
  // ⭐ М5: на ступени 5 перевод придерживаем до ответа — иначе задание решается чтением,
  // а не ухом (на ступени 3 он, наоборот, и есть задание). В разборе он появится.
  // ⭐⭐ М5.1: …но человек может открыть его КНОПКОЙ (или он открывается сам после
  // смыслового промаха) — правило считает `gap.showsPhraseRu`, здесь только рисуем.
  const showRu = showsPhraseRu(hearing, settled, hearRu);
  const aid = hearing && hearRu && !settled ? " aid" : "";
  html += '<div class="qex ctx' + (settled ? " ok" : "") + '">' +
    `<div class="en">${esc(spec.before)}${slot}${esc(spec.after)}</div>` +
    (showRu ? `<div class="ru${aid}">${esc(spec.ru)}</div>` : "") +
    "</div>";

  // ⭐⭐ М5.1 · КНОПКА-ОПОРА под заданием — ровно та же механика и тот же вид, что
  // «не слышно — показать слово» на ступени 1а. Трудность держим в задании, опору даём
  // кнопкой: видимой, необязательной и без штрафа (методика, разд. 3.6).
  if (hearing && !settled && !hearRu) {
    html += '<div class="listenskip">' +
      '<button class="linkbtn" type="button" data-act="ruhint">не понял фразу — показать перевод</button>' +
      "</div>";
  }

  if (!settled) {
    html += '<div class="opts">' + spec.options.map((o, i) => {
      const missed = gPicks.some((p) => p.toLowerCase() === o.toLowerCase());
      return `<button class="opt${missed ? " wrong" : ""}" type="button"${missed ? " disabled" : ""} ` +
        `data-gopt="${i}"><span class="mk">${i + 1}</span>${esc(o)}</button>`;
    }).join("") + "</div>";
    if (gHint) html += `<div class="gaphint">${gHint}</div>`;
    return html;
  }

  // Исход: показываем сам ответ, разбор слова и — с бокса 2 — длинный пример живьём.
  const sounds = pl.word || pl.phrase
    ? '<div class="sndrow ctxsnd">' +
      (pl.word ? snd(pl.word, "round", "Прослушать слово") : "") +
      (pl.phrase ? snd(pl.phrase, "pill", "Прослушать фразу", "Фраза") : "") +
      "</div>"
    : "";
  html += '<div class="reveal">' +
    `<div class="qtrans small">${esc(c.word)}` +
    (c.ipa ? ` <span class="ipa">${esc(c.ipa)}</span>` : "") + "</div>" +
    (c.translation ? `<div class="qalt">${esc(c.translation)}</div>` : "") +
    sounds +
    longExampleBlock(c, !!pl.phrase) +
    formsBlock(c) +
    collocBlock(c) +
    registerBlock(c) + noteBlock(c) +
    "</div>";
  return html;
}

/**
 * ⭐⭐ ТЕЛО СТУПЕНИ 6 «БЕГЛОСТЬ» (окно М6).
 *
 * Чем она отличается от «Тишины», которая стояла тут до сегодня: у «Тишины» человек сам
 * себя судил («показать ответ» → 0/1/2), то есть темпа не было вовсе — а беглость это
 * ровно про темп. Здесь есть видимое окно 5 секунд и два ответа: «Помню» / «не помню».
 * Оценку выводит ВРЕМЯ (`ladder.fluencyGrade`): быстрее 3 с → 2, 3–5 с → 1, не успел или
 * «не помню» → 0.
 *
 * ⭐ Пункт 3 окна · ПЛОТНОСТЬ ЭКРАНА ОТВЕТА: разбор здесь ровно ТРИ строки —
 * слово · перевод · кнопка звука. ⛔ Ни форм, ни коллокаций, ни заметки, ни одного из двух
 * примеров: экран на восемь блоков между карточками ломает то самое, что мы меряем. Разбор
 * не пропадает — ответивший «не помню» падает в бокс 0 и на следующем показе приходит
 * ступенью 1, где на разбор есть и время, и место. ⛔ Поэтому же `revealBlock` (разбор
 * ступеней 0–5) здесь не зовётся вовсе: у той функции своя плотность, и её мы не трогали.
 */
function fluencyBody(c: Card, pl: audio.Places): string {
  if (!settled) {
    // Вопрос — как у «Тишины» (ничего лишнего, транскрипции нет: она подсказка), плюс
    // секундомер. Сторону вопроса, как всегда, задало ЯДРО: на боксе 6 она RU→EN, то есть
    // английское слово человек достаёт из головы, а звука до ответа нет по общему закону.
    // Засечка «бегло» на полосе стоит там, где полоса окажется в момент порога, — место
    // считаем из тех же констант, чтобы правка порога двигала и подпись, и метку.
    const mark = (1 - ladder.FLUENCY_FAST_MS / ladder.FLUENCY_WINDOW_MS) * 100;
    return askBlock(c, "silence", pl.word) +
      '<div class="fltimer">' +
        `<div class="flnum">${ladder.fluencyLeft(0)}</div>` +
        `<div class="flbar" style="--flmark:${mark}%"><i style="width:100%"></i></div>` +
      "</div>" +
      '<div class="qtap flact">' +
        '<button class="tapbtn" type="button" data-act="flyes">Помню</button>' +
        '<button class="linkbtn" type="button" data-act="flno">не помню</button>' +
      "</div>";
  }

  // Исход: сперва честная цифра (это и есть обратная связь ступени), потом три строки.
  const secs = flMs === null ? "" : (flMs / 1000).toFixed(1).replace(".", ",");
  const verdict = flOut
    ? "время вышло"
    : flMs === null
      ? "не помню"
      : autoGrade === 2 ? `${secs} с — бегло` : `${secs} с — вспомнил`;
  const cls = autoGrade === 2 ? " ok" : autoGrade === 1 ? " slow" : " out";
  return `<div class="flres${cls}">${esc(verdict)}</div>` +
    '<div class="reveal fl">' +
      `<div class="qtrans">${esc(c.word)}` +
      (c.ipa ? ` <span class="ipa">${esc(c.ipa)}</span>` : "") + "</div>" +
      (c.translation ? `<div class="qalt">${esc(c.translation)}</div>` : "") +
      (pl.word ? `<div class="sndrow under">${snd(pl.word, "round", "Прослушать слово")}</div>` : "") +
    "</div>";
}

/**
 * Кнопки под карточкой.
 *  auto=false («Досье») — три оценки, человек судит себя сам;
 *  auto=true  («Выбор») — оценку вывели из ответа, поэтому оценивать нечего:
 *              ждём подтверждения из ядра и показываем «Дальше».
 * Подписи — что произойдёт со словом, а не «box 3».
 */
function gradesBlock(auto: boolean): string {
  const busy = sending ? " disabled" : "";
  if (sentGrade !== null) {
    const days = lastAnswer?.interval_days ?? 0;
    const when = days === 0 ? "вернём сегодня" : days === 1 ? "спросим завтра" : `спросим через ${days} ${plural(days, "день", "дня", "дней")}`;
    return `<div class="flash"><span class="arr">Записано</span> — ${when}</div>` +
      '<div class="grades one"><button class="grade good" type="button" data-act="next">' +
      '<div class="g">Дальше →</div></button></div>';
  }
  if (auto) {
    // Оценка уже определена ответом — либо она едет в ядро, либо не доехала.
    return sendError
      ? `<div class="sendfail">${esc(sendError)}</div>` +
        '<div class="grades one"><button class="grade warn" type="button" data-act="resend">' +
        '<div class="g">Отправить ещё раз</div></button></div>'
      : '<div class="grades one"><button class="grade good" type="button" disabled>' +
        '<div class="g">Записываем…</div></button></div>';
  }
  return '<div class="grades">' +
    `<button class="grade bad" type="button" data-grade="0"${busy}><div class="g">Не помню</div><div class="d">покажем сегодня ещё раз</div></button>` +

    `<button class="grade warn" type="button" data-grade="1"${busy}><div class="g">Трудно</div><div class="d">повторим совсем скоро</div></button>` +
    `<button class="grade good" type="button" data-grade="2"${busy}><div class="g">Легко</div><div class="d">вернёмся через пару дней</div></button>` +
    "</div>" +
    (sendError ? `<div class="sendfail">${esc(sendError)} · нажми оценку ещё раз</div>` : "");
}

/**
 * ⭐ М1, конец ступени 0: одна кнопка вместо трёх оценок. Себя человек тут не судит —
 * судить будет проверка, которая идёт следом на этой же карточке.
 */
function introBlock(next: Mode): string {
  // ⭐ М3: куда ведёт знакомство, зависит от слова — служебное идёт сразу на пропуск.
  const what = next === "gap" ? "вставь слово в предложение" : "выбери перевод";
  return `<div class="flash">Теперь проверим — ${what}</div>` +
    '<div class="grades one"><button class="grade good" type="button" data-act="met">' +
    '<div class="g">Дальше →</div></button></div>';
}

function cardScreen(): string {
  const c = deck[idx];
  const enRu = c.direction === "en_ru";
  // Тело собирается по ПОКАЗЫВАЕМОМУ виду: не собралось из готовых данных (нет 4 вариантов /
  // нет заготовки пропуска) или слово ещё новое, а выбран «Контекст» (закон окна 2.1) —
  // карточка уже перенаправлена в «Выбор»/«Досье».
  const m: Mode = shownMode(c);
  // ⭐ М2: «На слух» — тот же выбор из четырёх, отличается только вопрос (звук вместо текста).
  const picks = m === "choice" || m === "listen";
  const opts = picks ? choiceOptions(c) : null;
  const spec = m === "context" ? gapSpec(c) : null;
  const bspec = m === "build" ? buildFor(c) : null;
  const gspec = m === "gap" ? gapFor(c) : null;
  const hspec = m === "hear" ? hearFor(c) : null;
  const asDossier = m === "dossier" || m === "silence";
  // Где в этом виде и на этом шаге живёт звук — считает audio.places (⭐ закон + настройка).
  const pl = audio.places(c, m, opened());

  if (m === "fluency") {
    // ⭐ М6: подпись честно называет правило — «успей за 5 секунд». Число берём из
    // константы, чтобы подпись не разъехалась с порогом при первой же его правке.
    const secs = Math.round(ladder.FLUENCY_WINDOW_MS / 1000);
    const flashFl = `<span class="arr">Беглость</span> — своё слово, успей за ${secs} ` +
      plural(secs, "секунду", "секунды", "секунд");
    return head() + `<div class="flash">${flashFl}</div>` +
      '<div class="qcard">' + fluencyBody(c, pl) + "</div>" +
      (settled ? gradesBlock(true) : "");
  }

  if (m === "build" && bspec) {
    // Задание одно на обе стороны — «собери предложение»; отличается только доза внутри.
    const flashB = enRu
      ? '<span class="arr">Новое слово</span> — собери предложение из слов'
      : '<span class="arr">Знакомое слово</span> — собери предложение из слов';
    return head() + `<div class="flash">${flashB}</div>` +
      '<div class="qcard">' + buildBody(c, bspec, pl) + "</div>" +
      (settled ? gradesBlock(true) : "");
  }

  if (m === "hear" && hspec) {
    // ⭐ М5: подпись честно говорит, чем задан вопрос, — как на ступени 1а. Пустая
    // карточка со звуком пугает, если не сказать, что слушать её и есть задание.
    const flashHear = '<span class="arr">Фраза на слух</span> — послушай и вставь слово';
    return head() + `<div class="flash">${flashHear}</div>` +
      '<div class="qcard">' + gapBody(c, hspec, pl, true) + "</div>" +
      (settled ? gradesBlock(true) : "");
  }

  if (m === "gap" && gspec) {
    // ⭐ М3: подпись честно говорит, что спрашиваем. У служебного слова она другая:
    // «переведи» ему не подходит по построению — переводить там нечего.
    const flashGap = gspec.source === "function"
      ? '<span class="arr">Служебное слово</span> — оно живёт в обороте, а не в переводе'
      // ⭐⭐ Окно 1 запуска: оборот учим ЦЕЛЫМ КУСКОМ (второй этаж грамматики, разд. 3.4
      // методики), поэтому и плашка говорит про кусок, а не про отдельное слово.
      : gspec.source === "construction"
        ? '<span class="arr">Оборот</span> — учим целым куском, а не по словам'
        : '<span class="arr">Знакомое слово</span> — поставь его в предложение';
    return head() + `<div class="flash">${flashGap}</div>` +
      '<div class="qcard">' + gapBody(c, gspec, pl) + "</div>" +
      (settled ? gradesBlock(true) : "");
  }

  if (m === "context" && spec) {
    // Сюда доходит только окрепшее слово (закон окна 2.1) — подпись всегда одна.
    const flashCtx = '<span class="arr">Знакомое слово</span> — вспомни его и впиши';
    return head() + `<div class="flash">${flashCtx}</div>` +
      '<div class="qcard">' + contextBody(c, spec, pl) + "</div>" +
      (settled ? gradesBlock(true) : "");
  }

  let body = '<div class="qcard">' +
    (m === "listen" ? listenAsk(c, pl) : askBlock(c, m, pl.word));

  if (picks && opts) {
    if (settled) {
      // В2: звук у ПРАВИЛЬНОГО варианта — но только если он английский (вопрос был русский).
      // Английский вопрос уже озвучен кнопкой выше, второй кнопке там делать нечего.
      const atOption = !enRu ? pl.word : null;
      body += '<div class="opts">' + opts.options.map((o, i) => {
        const right = o.toLowerCase() === opts.correct.toLowerCase();
        const missed = wrongPicks.some((w) => w.toLowerCase() === o.toLowerCase());
        const cls = right ? " correct" : missed ? " wrong" : "";
        // Исход подведён — варианты больше не кнопки (в кнопку нельзя вложить кнопку звука).
        return `<div class="opt${cls}" data-opt="${i}">` +
          `<span class="mk">${i + 1}</span>${esc(o)}` +
          (right && atOption ? snd(atOption, "tiny", "Прослушать слово") : "") +
          "</div>";
      }).join("") + "</div>";
      body += revealBlock(c, { extras: true, phrase: pl.phrase });
    } else {
      body += '<div class="opts">' + opts.options.map((o, i) => {
        const missed = wrongPicks.some((w) => w.toLowerCase() === o.toLowerCase());
        return `<button class="opt${missed ? " wrong" : ""}" type="button"${missed ? " disabled" : ""} ` +
          `data-opt="${i}"><span class="mk">${i + 1}</span>${esc(o)}</button>`;
      }).join("") + "</div>";
    }
  } else if (revealed) {
    // «Тишина»: ничего лишнего — коллокаций и заметки нет, зато транскрипция
    // переезжает сюда (в вопросе её не показывали). Звук здесь появляется только
    // у русского вопроса: до раскрытия английского слова на экране не было.
    body += revealBlock(c, { extras: m === "dossier", ipa: m === "silence",
                             sound: enRu ? null : pl.word, phrase: pl.phrase });
  } else {
    body += '<div class="qtap">' +
      `<span class="hint">Вспомнил ${enRu ? "перевод" : "слово"}? Проверь себя</span>` +
      '<button class="tapbtn" type="button" data-act="reveal">Показать ответ</button></div>';
  }
  body += "</div>";

  // ⭐ М1: ступень 0 — знакомство. Подпись честная («знакомимся»), а вместо оценок внизу
  // одна кнопка «Дальше»: себя человек тут не судит, следом идёт проверка.
  const intro = m === "dossier" && !introDone && inIntro(c);
  const flash = intro
    ? '<span class="arr">Новое слово</span> — знакомимся: посмотри и послушай'
    : m === "listen"
      // ⭐ М2: подпись честно говорит, чем задан вопрос, — иначе пустая карточка пугает.
      ? '<span class="arr">На слух</span> — послушай и выбери перевод'
      : enRu
        ? '<span class="arr">Новое слово</span> — вспомни перевод'
        : '<span class="arr">Знакомое слово</span> — переведи на английский';

  const grades = intro
    ? (revealed ? introBlock(afterIntroMode(c)) : "")
    : (settled || revealed) ? gradesBlock(settled && !asDossier) : "";
  return head() + `<div class="flash">${flash}</div>` + body + grades;
}

/** Правая колонка на широком экране: контекст подхода и клавиши. */
function sideBlock(): string {
  const c = deck[idx];
  const left = Math.max(0, deck.length - idx - (settled || revealed ? 1 : 0));
  const today = state.me?.today;
  // ⭐ М1: показываем СТУПЕНЬ, а не «выбранный вид» — вид теперь следствие зрелости слова.
  const st = c ? step(c) : null;
  const shown = c ? shownMode(c) : "dossier";
  const stepRow = ladderOn() && st
    ? `<div class="kv"><span>Ступень</span><span>${esc(st.tag)}. ${esc(st.title)}</span></div>` +
      `<div class="kv"><span>Задание</span><span>${MODE_TITLE[shown]}</span></div>`
    : `<div class="kv"><span>Вид карточки</span><span>${MODE_TITLE[shown]}</span></div>`;
  return '<div class="card sidecard">' +
      '<p class="h">Подход</p>' +
      '<p class="hsub">Слова отобрало ядро: сперва глаголы, потом повторения, потом новые. ' +
      "Задание выбирается по зрелости слова.</p>" +
      stepRow +
      `<div class="kv"><span>Разобрано</span><span>${tally.total}</span></div>` +
      `<div class="kv"><span>Осталось в подходе</span><span>${left}</span></div>` +
      (startStats && startStats.in_queue > deck.length
        ? `<div class="kv"><span>Ещё ждут сегодня</span><span>${startStats.in_queue - deck.length}</span></div>`
        : "") +
      (today ? `<div class="kv"><span>Новых сегодня осталось</span><span>${today.new_left}</span></div>` : "") +
    "</div>" +
    '<div class="card sidecard kbd-hint">' +
      '<p class="h">С клавиатуры</p>' +
      '<div class="keys">' +
        // ⭐ М6: на беглости пробел отвечает «Помню» — тянуться мышкой там нельзя, ступень
        // меряет темп. Подпись обязана это называть, иначе правило есть только в коде.
        "<div><kbd>Пробел</kbd> показать ответ · «помню» · дальше</div>" +
        "<div><kbd>Enter</kbd> проверить · дальше</div>" +
        "<div><kbd>1</kbd><kbd>2</kbd><kbd>3</kbd> кнопки слева направо</div>" +
        "<div><kbd>1</kbd>…<kbd>4</kbd> вариант в «Выборе»</div>" +
      "</div>" +
    "</div>";
}

/**
 * ⭐⭐ «Ещё 5 новых» (окно 2 запуска, ч. 2) — блок добавки.
 *
 * Кнопка НАРОЧНО не такая же на вид, как «Позаниматься ещё»: та берёт уже назначенное
 * на сегодня, а эта ДОБАВЛЯЕТ работы на завтра — и человек должен видеть это ДО
 * нажатия, а не выяснять после. Отсюда пунктирная рамка и предупреждение прямо на ней.
 *
 * Блок стоит на ОБОИХ концах занятия — и на сводке подхода, и на «На сегодня всё»:
 * чаще всего добавки хочется именно тогда, когда очередь кончилась совсем.
 */
function extraBlock(stats: DailyStats | null): string {
  if (extra.canAsk(stats)) {
    const left = extra.leftNote(stats);
    return '<div class="extrablock">' +
      `<button class="extrabtn" type="button" id="extraBtn"${extraBusy ? " disabled" : ""}>` +
      `<span class="big">${esc(extra.label(stats))}</span>` +
      `<span class="warn">${esc(extra.warning())}</span>` +
      "</button>" +
      (left ? `<p class="morenote">${esc(left)}</p>` : "") +
      (extraNote ? `<p class="morenote">${esc(extraNote)}</p>` : "") +
      "</div>";
  }
  const line = extraNote || extra.capNote(stats);
  return line ? `<div class="extrablock"><p class="morenote">${esc(line)}</p></div>` : "";
}

function doneScreen(): string {
  const anyLeft = pending.length;
  const stats = pendingStats || state.me?.today || null;
  const newLeft = stats?.new_left ?? 0;
  const title = tally.again === 0 ? "Отличная работа!" : "Хорошая работа!";
  const sub = tally.again > 0
    ? `Слова, где было «не помню» (${tally.again}), вернутся сегодня ещё раз — так и работает память.`
    : "Слова уложены в память. Завтра закрепим.";

  const awards = earnedAll.length
    ? `<div class="awardline">🏆 ${esc(earnedAll.map((a) => a.title).join(" · "))}</div>` : "";

  // ⭐⭐ М6, пункт 2: РЕЗУЛЬТАТ СЕРИИ БЕГЛОСТИ. Считает витрина по своему же заходу —
  // ⛔ в БД не уезжает и схему не трогает (скорость это свойство захода, а не слова).
  // Строки нет вовсе, если ступень 6 в подходе не встретилась: пустых цифр не показываем.
  // ⚠ Единицу приклеиваем неразрывным пробелом: на 390 px «2,5 с» иначе рвётся, и одинокая
  // «с» уезжает на свою строку (поймано на стенде глазами, а не проверкой).
  const flLine = fluency.total
    ? '<div class="flserie">беглость: ' +
      `<b>${fluency.total}</b>&nbsp;${plural(fluency.total, "слово", "слова", "слов")}, ` +
      `<b>${fluency.fast}</b> быстрее ${Math.round(ladder.FLUENCY_FAST_MS / 1000)}&nbsp;с, среднее ` +
      (fluency.timed
        ? `<b>${(fluency.sumMs / fluency.timed / 1000).toFixed(1).replace(".", ",")}</b>&nbsp;с`
        : "<b>—</b>") +
      "</div>"
    : "";

  // ⭐ Ф2.5: что подход дал в опыте. Цифра — сумма прибавок ЯДРА, а не своя арифметика;
  // строки нет вовсе, если опыта не начислилось (например, слоя 3 в базе нет).
  const x = xpState();
  const xpLine = xpRun.gained
    ? '<div class="xpserie">' +
      `<b>${gainLabel(xpRun.gained)}</b>` +
      (xpRun.levelUp ? ` · новый уровень ${xpRun.levelUp}!` : ` · ${toNextLabel(x).toLowerCase()}`) +
      "</div>"
    : "";

  let more = "";
  if (anyLeft) {
    more = '<div class="moreblock">' +
      '<button class="cta" type="button" id="moreBtn" style="justify-content:center;margin-top:0">' +
      `<div><div class="big">Позаниматься ещё</div><div class="small">${anyLeft} ` +
      `${plural(anyLeft, "слово ждёт", "слова ждут", "слов ждут")}</div></div></button>` +
      (newLeft === 0
        ? '<p class="morenote"><b>Новые</b> слова на сегодня закончились — их число ограничено ' +
          "нарочно: если брать много новых разом, память их не удержит. А <b>повторять</b> " +
          "можно сколько угодно.</p>"
        : "") +
      "</div>";
  } else {
    more = '<div class="moreblock"><p class="morenote">На сегодня очередь пуста. ' +
      "Завтра слова придут сами — по расписанию памяти.</p></div>";
  }


  return head() +
    '<div class="qcard done">' +
      // ⭐ Ф2.5: на сводке стоит ТВОЙ герой на своём уровне, а не Лингви первого уровня.
      `<div class="em jump">${drawMascot(mascotLevel(x), false, x.character)}</div>` +
      awards +
      `<h3>${title}</h3><p>${sub}</p>` +
      '<div class="row">' +
        `<div><div class="b">${tally.total}</div><div class="l">разобрано</div></div>` +
        `<div><div class="b">${tally.easy}</div><div class="l">легко</div></div>` +
        `<div><div class="b">${tally.hard}</div><div class="l">трудно</div></div>` +
        `<div><div class="b">${tally.again}</div><div class="l">не помню</div></div>` +
      "</div>" +
      flLine +
      xpLine +
      more +
      extraBlock(stats) +
      '<button class="ghostbtn" type="button" data-go="cabinet">В кабинет</button>' +
    "</div>";
}

function emptyScreen(): string {
  const t = state.me?.today ?? null;
  return '<div class="stub">' +
    '<div class="ic"><svg viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg></div>' +
    "<h3>На сегодня всё</h3>" +
    `<p>Очередь пуста${t && t.answered_today ? `, сегодня разобрано ${t.answered_today}` : ""}. ` +
    "Новые слова ограничены нарочно — так они удерживаются в памяти. Завтра придут следующие.</p>" +
    // ⭐ Тут добавка и нужнее всего: очередь кончилась, а время у человека осталось.
    extraBlock(t) +
    '<button class="ghostbtn" type="button" data-go="cabinet" style="max-width:220px">В кабинет</button>' +
    "</div>";
}

export function renderSession(host: HTMLElement): void {
  let inner: string;
  if (phase === "card" && deck[idx]) inner = cardScreen();
  else if (phase === "done") inner = doneScreen();
  else if (phase === "empty") inner = emptyScreen();
  else if (phase === "error") {
    inner = '<div class="stub"><h3>Занятие не открылось</h3>' +
      `<p>${esc(failure)}</p>` +
      '<button class="ghostbtn" type="button" data-act="retry" style="max-width:220px">Попробовать снова</button></div>';
  } else {
    inner = '<div class="boot"><div class="boot-dot"></div><p>Собираем слова на сегодня…</p></div>';
  }

  const withSide = phase === "card";
  host.innerHTML =
    '<div class="two-col"><div class="col-main">' + inner + "</div>" +
    // Боковая колонка занятия — контекст ШИРОКОГО окна. На телефоне она бы висела
    // лишним блоком под карточкой, поэтому прячем её шириной контейнера (не платформой).
    (withSide ? `<div class="col-side only-wide">${sideBlock()}</div>` : "") +
    "</div>";

  wire(host);
  if (phase === "card" && deck[idx]) {
    // 🆕 Окно 5: приложение запоминает слово и ступень — это две строки контекста в
    // заявке «не работает», которых человек никогда не напишет сам.
    const c = deck[idx];
    noteCard(c.word, `ступень ${step(c).tag} · ${step(c).title}`);
    sound(deck[idx]);
    fluencyClock(deck[idx]);   // ⭐ М6: секундомер ступени 6 — живёт вне разметки, как звук
  } else {
    stopClock();
  }
}

/**
 * Звуковая часть кадра. Отдельно от разметки, потому что экран перерисовывается часто
 * (доехали свежие цифры, сменилась тема), а проговорить слово само нужно РОВНО ОДИН раз.
 */
function sound(c: Card): void {
  audio.sync();                                  // состояния кнопок: ждёт · тянется · играет
  const m = shownMode(c);
  const pl = audio.places(c, m, opened());
  if (!spoken && audio.speak(pl)) {               // ⛔ звук выключен / «само» выключено → тишина
    spoken = true;
    // прозвучало само → единственная кнопка вопроса сразу говорит «Ещё раз» (М2 и М5)
    if (m === "listen" || m === "hear") markHeard();
  }

  // ⭐ М2: на ступени 1а звук — единственный вопрос, поэтому файл проверяем ЗАРАНЕЕ, а не
  // ждём, пока человек нажмёт в пустоту. Не дотянулся (404 или сеть) — молча показываем
  // написание: карточка становится ступенью 1 и вопрос у неё есть. Проверяем один раз
  // на карточку: результат кеширует сам плеер.
  if (m === "listen" && !listenProbed && !listenSeen) {
    listenProbed = true;
    void audio.ensure(pl.word).then((fine) => {
      if (fine || deck[idx] !== c || listenSeen) return;
      listenSeen = true;
      render("session");
    });
  }
  // ⭐ М5: то же самое для ступени 5, только файл ФРАЗЫ. Не доехал — плеер помечает его
  // отсутствующим, и на перерисовке лестница молча отдаёт ступень 4: человек видит
  // обычный «Контекст», а не карточку, которую нечем озвучить.
  if (m === "hear" && !hearProbed) {
    hearProbed = true;
    void audio.ensure(pl.phrase).then((fine) => {
      if (fine || deck[idx] !== c) return;
      render("session");
    });
  }

  const next = deck[idx + 1];
  if (next && preloadedFor !== next.word_id) {
    preloadedFor = next.word_id;                 // следующая карточка зазвучит без задержки
    audio.preload(audio.preloadRefs(next));
  }
}

/* ── обработчики ──────────────────────────────────────────────────────────── */

function wire(host: HTMLElement): void {
  host.querySelector<HTMLElement>('[data-act="reveal"]')?.addEventListener("click", (e) => {
    tap(e.currentTarget as HTMLElement);
    reveal();
  });
  host.querySelector<HTMLElement>('[data-act="next"]')?.addEventListener("click", (e) => {
    tap(e.currentTarget as HTMLElement);
    next();
  });
  host.querySelector<HTMLElement>('[data-act="met"]')?.addEventListener("click", (e) => {
    tap(e.currentTarget as HTMLElement);
    metWord();
  });
  host.querySelector<HTMLElement>('[data-act="hearskip"]')?.addEventListener("click", (e) => {
    tap(e.currentTarget as HTMLElement, "light");
    showSpelling();
  });
  /* ⭐ М5.1: опора ступени 5 — открыть перевод фразы. Оценки не касается вовсе. */
  host.querySelector<HTMLElement>('[data-act="ruhint"]')?.addEventListener("click", (e) => {
    tap(e.currentTarget as HTMLElement, "light");
    showPhraseRu();
  });
  /* ⭐⭐ М6: два ответа ступени 6. Оценку ставит ВРЕМЯ, а не кнопка, — поэтому обе ведут
     в один обработчик, и он первым делом снимает показание секундомера. */
  host.querySelector<HTMLElement>('[data-act="flyes"]')?.addEventListener("click", (e) => {
    tap(e.currentTarget as HTMLElement, "success");
    fluencyAnswer(true);
  });
  host.querySelector<HTMLElement>('[data-act="flno"]')?.addEventListener("click", (e) => {
    tap(e.currentTarget as HTMLElement, "light");
    fluencyAnswer(false);
  });
  host.querySelector<HTMLElement>('[data-act="retry"]')?.addEventListener("click", () => void load());
  host.querySelector<HTMLElement>('[data-act="resend"]')?.addEventListener("click", (e) => {
    tap(e.currentTarget as HTMLElement);
    if (autoGrade !== null) void send(autoGrade);
  });
  host.querySelector<HTMLElement>("#moreBtn")?.addEventListener("click", (e) => {
    tap(e.currentTarget as HTMLElement);
    clearFx();
    startDeck(pending, pendingStats);
  });
  host.querySelector<HTMLElement>("#extraBtn")?.addEventListener("click", (e) => {
    tap(e.currentTarget as HTMLElement);
    void askExtra();
  });
  host.querySelectorAll<HTMLElement>("[data-grade]").forEach((b) => {
    b.addEventListener("click", () => {
      tap(b, b.dataset.grade === "2" ? "success" : "light");
      grade(Number(b.dataset.grade) as 0 | 1 | 2);
    });
  });
  /* Звук: нажатие = ещё и разрешение браузера (если его до сих пор не было). */
  host.querySelectorAll<HTMLElement>("button.snd[data-snd]").forEach((b) => {
    b.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();          // кнопка живёт внутри вариантов и строк — клик её, не их
      tap(b);
      audio.unlock();
      if (b.classList.contains("hear")) markHeard();
      void audio.play(b.dataset.snd || "");
    });
  });

  host.querySelectorAll<HTMLElement>("button.opt[data-opt]:not(:disabled)").forEach((b) => {
    b.addEventListener("click", () => {
      const c = deck[idx];
      const opts = c ? choiceOptions(c) : null;      // порядок стабилен: зерно = word_id
      const chosen = opts?.options[Number(b.dataset.opt)];
      if (!chosen) return;
      tap(b);
      pick(chosen);
    });
  });

  /* ⭐ М3 «Пропуск»: те же кнопки-варианты, только собраны из форм слова. */
  host.querySelectorAll<HTMLElement>("button.opt[data-gopt]:not(:disabled)").forEach((b) => {
    b.addEventListener("click", () => {
      const c = deck[idx];
      const spec = c ? activeGap(c) : null;          // порядок стабилен: зерно = word_id
      const chosen = spec?.options[Number(b.dataset.gopt)];
      if (!chosen) return;
      tap(b);
      pickGap(chosen);
    });
  });

  /* «Собери фразу»: набор слов и строка сборки. Тап по слову в наборе — выложить,
     тап по выложенному — вернуть обратно. Клавиатура не участвует вообще. */
  host.querySelectorAll<HTMLElement>(".bank .btok[data-put]:not(:disabled)").forEach((b) => {
    b.addEventListener("click", () => {
      tap(b);
      putWord(Number(b.dataset.put));
    });
  });
  host.querySelectorAll<HTMLElement>(".bline .btok[data-take]").forEach((b) => {
    b.addEventListener("click", () => {
      tap(b, "light");
      takeWord(Number(b.dataset.take));
    });
  });
  host.querySelector<HTMLElement>('[data-act="bcheck"]')?.addEventListener("click", (e) => {
    tap(e.currentTarget as HTMLElement);
    checkPhrase();
  });
  host.querySelector<HTMLElement>('[data-act="bidk"]')?.addEventListener("click", (e) => {
    tap(e.currentTarget as HTMLElement);
    giveUpPhrase();
  });

  /* «Контекст»: поля пропуска. Напечатанное держим в состоянии модуля — перерисовка
     экрана (пришли свежие цифры, сменилась тема) не должна стирать набранное. */
  const gaps = host.querySelectorAll<HTMLInputElement>(".gapin[data-gap]");
  gaps.forEach((el) => {
    const g = Number(el.dataset.gap);
    el.addEventListener("input", () => {
      gapText[g] = el.value;
      el.classList.remove("miss");        // начал править — красную метку убираем
    });
    el.addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      const nextEmpty = [...gaps].find((x) => x !== el && !x.value.trim());
      if (nextEmpty) { nextEmpty.focus(); return; }   // оборот из двух слов: сперва добери второе
      checkGap();
    });
    el.addEventListener("focus", () => keepVisible(el));
  });
  host.querySelector<HTMLElement>('[data-act="check"]')?.addEventListener("click", (e) => {
    tap(e.currentTarget as HTMLElement);
    checkGap();
  });
  host.querySelector<HTMLElement>('[data-act="idk"]')?.addEventListener("click", (e) => {
    tap(e.currentTarget as HTMLElement);
    giveUpGap();
  });
  if (gaps.length) focusGap();
}

/* ── поле ввода и клавиатура телефона ─────────────────────────────────────── */

/** Курсор в первый незаполненный пропуск (или в тот, где ошиблись). */
function focusGap(nudge = false): void {
  const gaps = [...document.querySelectorAll<HTMLInputElement>(".gapin[data-gap]")];
  if (!gaps.length) return;
  const target = gaps.find((el) => !el.value.trim()) || gaps[gaps.length - 1];
  if (document.activeElement === target && !nudge) { keepVisible(target); return; }
  try { target.focus({ preventScroll: true }); } catch { target.focus(); }
  const end = target.value.length;
  try { target.setSelectionRange(end, end); } catch { /* не все клиенты умеют */ }
  if (nudge) {
    target.classList.add("shake");
    setTimeout(() => target.classList.remove("shake"), 420);
  }
  keepVisible(target);
}

/**
 * Клавиатура телефона перекрывает низ приложения: наша высота (`--app-h`) считается
 * по СТАБИЛЬНОЙ высоте вьюпорта, то есть без клавиатуры, и Telegram окно не сжимает.
 * Поэтому поле поднимаем сами — в середину видимой части, уже после того как
 * клавиатура выехала (иначе браузер посчитает не ту геометрию).
 */
function keepVisible(el: HTMLElement): void {
  setTimeout(() => {
    try { el.scrollIntoView({ block: "center", behavior: "smooth" }); }
    catch { el.scrollIntoView(); }
  }, 280);
}

/* ── ⭐ зум на время ввода (окно 2.1) ─────────────────────────────────────────
   Живая обкатка 26.07: WebView внутри Telegram приближает экран при фокусе на поле
   ДАЖЕ при шрифте 16px (порог 16px спасает только Safari) — и обратно не отматывает:
   один срыв, и всё приложение остаётся увеличенным.
   Лечение — запрет зума РОВНО НА ВРЕМЯ ВВОДА: на фокус поля дописываем в мету viewport
   `maximum-scale=1`, на blur снимаем. ⛔ Глухого запрета навсегда не ставим: он отнимает
   пинч-зум у того, кому нужно разглядеть, — это доступность, а не мелочь.
   ⚠ Ставить надо ДО фокуса (после него браузер уже решил приблизить), поэтому ловим и
   касание/нажатие по полю в фазе перехвата, а `focusin` оставляем страховкой. */

const VIEWPORT_LOCK = "maximum-scale=1, user-scalable=no";
let viewportBase = "";
let unlockTimer = 0;

function viewportMeta(): HTMLMetaElement | null {
  return document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
}

/** Запрет зума: включить (на время ввода) или снять (когда поле отпустили). */
function zoomLock(on: boolean): void {
  const meta = viewportMeta();
  if (!meta) return;
  if (!viewportBase) viewportBase = meta.content.replace(/,\s*(maximum-scale|user-scalable)[^,]*/g, "");
  const want = on ? `${viewportBase}, ${VIEWPORT_LOCK}` : viewportBase;
  if (meta.content !== want) meta.content = want;   // мету перечитывают только при смене
}

/** Это поле ввода, из-за которого браузер и зумит? (у нас такое одно — пропуск). */
function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
}

function lockZoomWhileTyping(): void {
  const lock = (e: Event): void => {
    if (!isTypingTarget(e.target)) return;
    clearTimeout(unlockTimer);
    zoomLock(true);
  };
  // Касание/нажатие ловим ДО фокуса и в перехвате — иначе запрет опаздывает на кадр.
  document.addEventListener("touchstart", lock, { capture: true, passive: true });
  document.addEventListener("pointerdown", lock, true);
  document.addEventListener("focusin", lock, true);
  document.addEventListener("focusout", (e) => {
    if (!isTypingTarget(e.target)) return;
    // Переход из первого пропуска во второй — это тоже focusout: снимаем запрет,
    // только если через мгновение в поле никто не вернулся (иначе мигаем метой зря).
    clearTimeout(unlockTimer);
    unlockTimer = window.setTimeout(() => {
      if (!isTypingTarget(document.activeElement)) zoomLock(false);
    }, 200);
  }, true);
}
lockZoomWhileTyping();

/**
 * Сколько именно занимает клавиатура — знает visualViewport. Кладём это в `--kb`
 * (внизу появляется запас, чтобы поле было куда поднять) и убираем «остров»
 * навигации, пока человек печатает: он всё равно накрыт клавиатурой.
 */
function watchKeyboard(): void {
  const vv = window.visualViewport;
  if (!vv) return;
  const sync = (): void => {
    const hidden = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    document.documentElement.style.setProperty("--kb", `${Math.round(hidden)}px`);
    document.body.classList.toggle("kb-open", hidden > 120);
  };
  vv.addEventListener("resize", sync);
  vv.addEventListener("scroll", sync);
  sync();
}
watchKeyboard();

/**
 * Горячие клавиши на ПК: пробел/Enter — главное действие (показать ответ · проверить ·
 * дальше), цифры — кнопки слева направо (варианты в «Выборе», оценки в «Досье»).
 * Пока курсор в поле пропуска, клавиши обрабатывает само поле — здесь мы молчим.
 */
document.addEventListener("keydown", (e) => {
  if (state.screen !== "session") return;
  const target = e.target as HTMLElement | null;
  if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
  if (e.metaKey || e.ctrlKey || e.altKey) return;

  const click = (sel: string): boolean => {
    const el = document.querySelector<HTMLElement>(sel);
    if (!el || (el as HTMLButtonElement).disabled) return false;
    el.click();
    return true;
  };

  if (e.key === " " || e.key === "Enter") {
    // ⭐ М2: на карточке «На слух» главное действие — послушать ещё раз, поэтому кнопка
    // звука стоит в этой цепочке первой (пока она на экране, других действий там нет).
    // ⭐ М6: на беглости главное действие — «Помню», и оно должно быть под пробелом:
    // ступень меряет темп, а тянуться мышкой к кнопке — это лишние полсекунды.
    if (click("button.snd.hear") || click('[data-act="flyes"]') ||
        click('[data-act="reveal"]') || click('[data-act="check"]') ||
        click('[data-act="bcheck"]') || click('[data-act="met"]') ||
        click('[data-act="next"]') || click("#moreBtn")) e.preventDefault();
    return;
  }
  if (e.key === "Backspace") {
    // «Собери фразу» на ПК: забой снимает последнее выложенное слово.
    const line = [...document.querySelectorAll<HTMLElement>(".bline .btok[data-take]")];
    if (line.length) { line[line.length - 1].click(); e.preventDefault(); }
    return;
  }
  if (/^[1-4]$/.test(e.key)) {
    const n = Number(e.key) - 1;
    if (click(`button.opt[data-opt="${n}"]:not(:disabled)`) ||
        click(`button.opt[data-gopt="${n}"]:not(:disabled)`)) { e.preventDefault(); return; }
    if (n <= 2 && click(`[data-grade="${n}"]`)) e.preventDefault();
  }
});
